// Alle sichtbaren Strings des Editors, auf Deutsch.
//
// Verschachtelt nach Bereich, damit ein Schlüssel wie `layers.deleteLayer`
// sagt, wo er hingehört. Ein Paar `{ one, other }` ist eine Mehrzahlform für
// `tn()`; `{count}` ist dort die Zahl, andere `{platzhalter}` kommen aus den
// Parametern von `t()`.
//
// Ton wie Landing und Konto (docs/BRAND.md): du-Ansprache, deutsche
// Anführungszeichen „…", Halbgeviertstrich —. Fachbegriffe: Ebene, Pfad,
// Knoten, Vektorisieren (nie „Nachzeichnen"), Neu vektorisieren, Verschmelzen,
// Füllung, Kontur, Zuschneiden, Pinsel, Radierer.

export const de = {
  common: {
    cancel: 'Abbrechen',
    close: 'Schließen',
    confirm: 'Bestätigen',
    save: 'Speichern',
    delete: 'Löschen',
    done: 'Fertig',
    dismiss: 'Ausblenden',
    retry: 'Erneut versuchen',
    reload: 'Neu laden',
    upgradeToPro: 'Upgrade auf Pro',
    or: 'oder',
    // Die Guthaben-Zeile an beiden Auslösestellen für KI-Requests (KI-Panel,
    // Motiv generieren im Einfügen-Dialog) — deshalb hier und nicht in einem
    // der beiden Abschnitte. „Requests" wie auf der Preisseite und in
    // ai.editLimit, nicht übersetzen.
    aiRequestsLeft: 'Noch {remaining} von {limit} KI-Requests diesen Monat',
    aiRequestsLeftWithCredits: 'Noch {remaining} von {limit} KI-Requests diesen Monat · Guthaben: {credits}',
  },

  save: {
    saved: 'Gespeichert',
    saving: 'Speichert…',
    dirty: 'Ungespeicherte Änderungen',
    conflict: 'Nicht gespeichert — Konflikt',
    error: 'Nicht gespeichert — Fehler',
    offline: 'Offline — wird gespeichert, sobald du wieder online bist',
    // Kürzer, weil es auf dem Telefon als Untertitel unter dem Namen steht.
    shortSaved: 'Gespeichert',
    shortSaving: 'Speichert…',
    shortDirty: 'Ungespeichert',
    shortConflict: 'Konflikt',
    shortError: 'Nicht gespeichert',
    shortOffline: 'Offline',
  },

  topbar: {
    projects: 'Projekte',
    backToProjects: 'Zurück zu den Projekten',
    projectName: 'Projektname',
    undo: 'Rückgängig',
    redo: 'Wiederholen',
    zoomIn: 'Vergrößern',
    zoomOut: 'Verkleinern',
    zoom: 'Zoom',
    zoomTo100: 'Auf 100 % zoomen',
    fitToView: 'Einpassen',
    shortcuts: 'Tastenkürzel',
    history: 'Versionen…',
    hidePanels: 'Seitenleiste ausblenden',
    showPanels: 'Seitenleiste einblenden',
    rulers: 'Lineale',
    publish: 'Veröffentlichen',
    updateTemplate: 'Vorlage aktualisieren',
    viewTemplate: 'Ansehen',
    // Der „Vorschlagen"-Knopf neben dem Namensfeld (Task 3): ein
    // KI-Namensvorschlag, den die Nutzerin noch bestätigen muss.
    suggestName: 'Namen vorschlagen',
    suggestNamePro: 'Namensvorschläge sind eine Pro-Funktion.',
    suggestNameLimit: 'Das Tageslimit für KI-Bilder ist erreicht. Morgen geht es weiter.',
    suggestNameUnavailable: 'Der Namensvorschlag ist gerade nicht verfügbar. Versuch es später noch einmal.',
    suggestNameNoImage: 'Ohne Bild gibt es nichts vorzuschlagen.',
    suggestNameFailed: 'Der Namensvorschlag ist fehlgeschlagen. Versuch es noch einmal.',
    // Tooltip: „Rückgängig · Strg/⌘+Z", title: „Rückgängig (Strg/⌘+Z)".
    tip: '{label} · {hint}',
    title: '{label} ({hint})',
  },

  sidebar: {
    tabsLabel: 'Panel-Gruppen',
    tabs: {
      ebenen: 'Ebenen',
      bearbeiten: 'Bearbeiten',
      bild: 'Bild',
      export: 'Export',
    },
  },

  // Die vier Klapp-Abschnitte im Bearbeiten-Tab (editor/editSections.ts). Die
  // Namen der Panels darin bleiben, wie sie sind — diese hier stehen darüber.
  editSections: {
    pfadForm: 'Pfad & Form',
    musterEffekte: 'Muster & Effekte',
    anordnenBogen: 'Anordnen & Bogen',
    aufraeumen: 'Aufräumen',
  },

  tools: {
    select: 'Auswählen',
    hand: 'Hand',
    measure: 'Messen',
    knife: 'Messer',
    freehandKnife: 'Freihand-Messer',
    node: 'Pfad bearbeiten',
    eyedropper: 'Pipette',
    bucket: 'Farbeimer',
    autofill: 'Smarte Füllung',
    brush: 'Pinsel',
    eraser: 'Radierer',
    crop: 'Zuschneiden',
    rect: 'Rechteck',
    roundRect: 'Abgerundetes Rechteck',
    ellipse: 'Ellipse',
    line: 'Linie',
    polygon: 'Vieleck / Stern',
    heart: 'Herz',
    arrow: 'Pfeil',
    banner: 'Banner',
    frame: 'Rahmen',
    pen: 'Stift',
    pencil: 'Bleistift',
    addText: 'Text hinzufügen',
    railLabel: 'Werkzeuge',
    activeColor: 'Aktive Farbe',
  },

  shortcuts: {
    dialogTitle: 'Tastenkürzel',
    groups: {
      tools: 'Werkzeuge',
      view: 'Ansicht',
      edit: 'Bearbeiten',
      selection: 'Auswahl',
      shape: 'Form',
      nodes: 'Knoten',
      bitmap: 'Bitmap',
      help: 'Hilfe',
    },
    keys: {
      mod: 'Strg/⌘',
      shift: 'Umschalt',
      alt: 'Alt',
      space: 'Leertaste',
      delete: 'Entf',
      backspace: 'Rücktaste',
      enter: 'Eingabe',
      escape: 'Esc',
    },
    labels: {
      holdToPan: 'Gedrückt halten zum Verschieben',
      zoomIn: 'Vergrößern',
      zoomOut: 'Verkleinern',
      zoomTo100: 'Auf 100 % zoomen',
      zoomToFit: 'Seite in die Ansicht einpassen',
      undo: 'Rückgängig',
      redo: 'Wiederholen',
      copySelection: 'Ausgewählte Pfade kopieren',
      pasteClipboard: 'In die aktive Ebene einfügen',
      duplicateSelection: 'Auswahl duplizieren',
      flipHorizontal: 'Auswahl horizontal spiegeln',
      flipVertical: 'Auswahl vertikal spiegeln',
      bringForward: 'Auswahl eine Stufe nach vorn',
      sendBackward: 'Auswahl eine Stufe nach hinten',
      bringToFront: 'Auswahl ganz nach vorn (in ihrer Ebene)',
      sendToBack: 'Auswahl ganz nach hinten (in ihrer Ebene)',
      selectAll: 'Alle sichtbaren Pfade auswählen',
      nudge: 'Verschieben — 0,5 mm, mit Umschalt 5 mm',
      deleteSelection: 'Löschen — im Pfad-Editor die gewählten Knoten, sonst die Pfade',
      cancel: 'Zurück zum Pfeil — und außerhalb des Pfad-Editors die Auswahl aufheben',
      finishEdit: 'Pfad-Editor verlassen — oder den Zuschnitt anwenden, solange das Zuschneiden aktiv ist',
      stepBrushDown: 'Pinsel ein Zehntel kleiner',
      stepBrushUp: 'Pinsel ein Zehntel größer',
      toggleRulers: 'Lineale ein- oder ausblenden',
      toggleGrid: 'Raster ein- oder ausblenden',
      toggleSnap: 'Fang ein- oder ausschalten',
      showShortcuts: 'Diese Liste zeigen',
      showHistory: 'Versionen zeigen',
      uniteSelection: 'Ausgewählte Formen vereinen',
      subtractSelection: 'Übrige Formen von der zuerst gewählten abziehen',
      intersectSelection: 'Schnittmenge der ausgewählten Formen',
      excludeSelection: 'Überlappung ausschließen',
      groupSelection: 'Auswahl gruppieren (anheften)',
      ungroupSelection: 'Gruppierung aufheben',
    },
    note: 'Tastenkürzel sind aus, solange du in ein Feld tippst. Die Maus hat zwei eigene: ein Doppelklick öffnet eine Form zum Bearbeiten der Knoten, und Alt beim Ziehen eines Bézier-Griffs bewegt nur diesen Griff.',
  },

  // Die Coachmark-Tour beim ersten Besuch des Editors (tour.ts, TourOverlay.tsx).
  // Objektgruppen (Anheften): Strg+G im Editor, siehe objectGroups.ts.
  objectGroups: {
    name: 'Gruppe',
    tooFew: 'Wähle mindestens zwei Objekte aus, um sie zu gruppieren.',
    ungrouped: 'Gruppierung aufgehoben.',
  },

  // Zeichenflächen: mehrere Blätter in einem Projekt (artboards.ts,
  // ArtboardSwitcher.tsx). Die Hauptfläche ist die erste; sie ist, was
  // Vorschaubild, Vorlage und Teilen-Link zeigen.
  artboards: {
    title: 'Zeichenflächen',
    hint: 'Mehrere Blätter in einem Projekt — Vorder- und Rückseite, Größen, Varianten. Jede Fläche wird einzeln exportiert.',
    unnamed: 'Hauptfläche',
    primaryBadge: 'Haupt',
    switchTo: 'Zeichenfläche „{name}" anzeigen',
    add: 'Zeichenfläche hinzufügen',
    addPromptTitle: 'Neue Zeichenfläche',
    addPromptLabel: 'Name der Zeichenfläche',
    addConfirm: 'Anlegen',
    defaultName: 'Zeichenfläche {n}',
    limit: 'Höchstens {max} Zeichenflächen je Projekt.',
    noSize: 'Das Projekt hat noch keine Größe — lade erst ein Bild hoch oder vektorisiere neu.',
    rename: 'Zeichenfläche umbenennen',
    renamePromptTitle: 'Zeichenfläche umbenennen',
    renamePromptLabel: 'Name der Zeichenfläche',
    renameConfirm: 'Umbenennen',
    duplicate: 'Zeichenfläche duplizieren',
    delete: 'Zeichenfläche löschen',
    deleteConfirmTitle: 'Zeichenfläche löschen',
    deleteConfirmBody: 'Löschen von „{name}" löscht auch alle Ebenen darauf. Das lässt sich nicht rückgängig machen.',
    deleteConfirm: 'Zeichenfläche löschen',
    makePrimary: 'Zur Hauptfläche machen',
    makePrimaryHint: 'Die Hauptfläche zeigen Vorschaubild, Vorlage und Teilen-Link; ihre Größe ist die des Projekts.',
    exportNote: 'Exportiert wird die Zeichenfläche „{name}". Die anderen Flächen exportierst du einzeln, jede über ihren Reiter.',
    publishNote: 'Veröffentlicht wird die Zeichenfläche „{name}" — eine Vorlage ist ein Motiv.',
    retraceNote: 'Neu vektorisieren ersetzt die Hauptfläche; die anderen Zeichenflächen bleiben, wie sie sind.',
  },

  tour: {
    start: 'Tour starten',
    progress: 'Schritt {step} von {total}',
    next: 'Weiter',
    prev: 'Zurück',
    finish: 'Fertig',
    skip: 'Überspringen',
    toolbar: {
      title: 'Deine Werkzeuge',
      body: 'Hier wählst du, womit du gerade arbeitest — Auswählen, Stift, Pinsel und die anderen. Ein Kürzel steht immer daneben.',
    },
    layers: {
      title: 'Ebenen',
      body: 'Jede Farbe deines Motivs ist eine eigene Ebene. Hier blendest du sie ein oder aus, benennst sie um und sortierst sie.',
    },
    edit: {
      title: 'Bearbeiten',
      body: 'Pfade glätten, Formen vereinen, ein Blatt anordnen — alles, was am Vektor selbst etwas ändert, wohnt hier.',
    },
    image: {
      title: 'Bild',
      body: 'Das Ausgangsbild retuschieren oder mit anderen Einstellungen neu vektorisieren — dafür ist dieser Tab.',
    },
    export: {
      title: 'Export',
      body: 'Wenn dein Schnitt fertig ist: hier lädst du ihn für deinen Plotter herunter.',
    },
  },

  editor: {
    loading: 'Projekt wird geladen…',
    loadError: 'Das Projekt konnte nicht geladen werden.',
    bundleError: 'Der Editor konnte nicht geladen werden. Bitte lade die Seite neu.',
    reloadError: 'Das Projekt konnte nicht neu geladen werden.',
    conversionFailedNoDocument: 'Diese Vektorisierung ist fehlgeschlagen. Vektorisiere das Bild neu, um es noch einmal zu versuchen.',
    conversionStatus: 'Status der Vektorisierung: {status}',
    conversionFailed: 'Die letzte Vektorisierung ist fehlgeschlagen. Du siehst das vorherige Ergebnis.',
    conversionRunning: 'Eine Vektorisierung läuft ({status}). Diese Ansicht aktualisiert sich, sobald sie fertig ist.',
    retraceNotice: 'Die Vektor-Ebenen wurden durch die neue Vektorisierung ersetzt.',
    conflict: 'Das Projekt wurde woanders geändert — Neu laden verwirft, was du hier seit dem letzten Speichern geändert hast.',
    saveError: 'Deine Änderungen konnten nicht gespeichert werden. Sie sind noch da, aber nur in diesem Tab.',
    offlineBanner: 'Du bist offline. Du kannst weiter bearbeiten — gespeichert wird, sobald die Verbindung zurück ist.',
    offlineLoaded: 'Ohne Verbindung geöffnet: Das ist der Stand, den dieses Gerät zuletzt gesehen hat.',
    offlineLoadError: 'Du bist offline, und dieses Projekt wurde auf diesem Gerät noch nicht geöffnet.',
    emptyTitle: 'Dieses Dokument hat keine Ebenen',
    emptyBody: 'Lade ein Bild hoch oder vektorisiere neu — das Panel „Neu vektorisieren" vektorisiert das Bild unter der Zeichenfläche noch einmal und füllt die Ebenen.',
    cutPrompt: 'Hat der Schnitt geklappt?',
    cutPromptAction: 'Schnittbericht schreiben',
  },

  layers: {
    title: 'Ebenen',
    empty: 'Noch keine Ebenen. Lade ein Bild hoch oder vektorisiere neu.',
    paths: { one: '1 Pfad', other: '{count} Pfade' },
    nodes: { one: '1 Knoten', other: '{count} Knoten' },
    reorder: '{name} verschieben',
    dragToReorder: 'Ziehen zum Umsortieren',
    visibilityTitle: '{action} — Alt-Klick für Solo',
    show: 'Einblenden',
    hide: 'Ausblenden',
    showLayer: '{name} einblenden',
    replaceColor: '{color} ersetzen',
    colorOf: 'Farbe von {name}',
    rename: '{name} umbenennen',
    roleTitle: 'Wechselt zwischen Gravur (Füllung) und Schnitt (Kontur)',
    roleFill: 'Gravur',
    roleStroke: 'Schnitt',
    lineStyleOf: 'Linienart von {name}',
    lineStyleTitle: 'Wie die Linien dieser Schnittebene geschnitten werden',
    lineStyleLabel: 'Linienart: {name}',
    lineStyleSolid: 'Durchgezogen',
    lineStyleDashed: 'Gestrichelt (Falz)',
    lineStylePerforated: 'Perforiert',
    mergeInto: '{name} zusammenführen mit',
    mergeTitle: 'Diese Ebene in eine andere zusammenführen',
    weldIn: 'Formen in {name} verschmelzen',
    invertIn: '{name} invertieren (Negativform)',
    invertTitle: 'Negativform: ein Rahmen 5 mm um das Motiv, das Motiv wird zum Loch — die Schablone',
    inverted: '{name} invertiert',
    weldTitle: 'Überlappende Formen in dieser Ebene verschmelzen',
    welded: '{name} verschmolzen',
    editText: 'Text bearbeiten',
    editTextOf: 'Text von {name} bearbeiten',
    duplicate: 'Ebene duplizieren',
    duplicateLayer: '{name} duplizieren',
    delete: 'Ebene löschen',
    deleteLayer: '{name} löschen',
    deleteConfirmTitle: 'Ebene löschen',
    deleteConfirmBody: 'Löschen von „{name}" löscht auch {paths} darin.',
    deleteConfirm: 'Ebene löschen',
    // Was ein Duplikat heißt: „Rot Kopie", wie es die großen Programme halten.
    copySuffix: '{name} Kopie',
    add: 'Neue Ebene',
    addTitle: 'Leere Ebene über der aktiven anlegen',
    addName: 'Ebene {n}',
    limit: 'Mehr Ebenen passen nicht in ein Dokument.',
    // Gruppen: ein Etikett auf der Ebene, unter dem die Liste benachbarte
    // Ebenen desselben Namens zusammenfasst.
    group: 'Gruppieren',
    groupTitle: 'Aktive Ebene in eine Gruppe legen',
    groupPromptTitle: 'Ebenen gruppieren',
    groupPromptLabel: 'Name der Gruppe',
    groupToast: { one: '1 Ebene in „{group}"', other: '{count} Ebenen in „{group}"' },
    groupAssign: 'Gruppe von {name}',
    groupAssignTitle: 'Diese Ebene in eine Gruppe legen',
    groupNone: 'Aus der Gruppe nehmen',
    groupCount: { one: '1 Ebene', other: '{count} Ebenen' },
    groupedPaths: { one: '1 Pfad in einer Objektgruppe', other: '{count} Pfade in Objektgruppen' },
    groupCollapse: 'Gruppe {name} zuklappen',
    groupExpand: 'Gruppe {name} aufklappen',
    groupVisibility: 'Gruppe {name} ein- oder ausblenden',
    ungroup: 'Gruppe auflösen',
    ungroupOf: 'Gruppe {name} auflösen',
    sortByColor: 'Nach Farbe ordnen',
    sortByColorTitle: 'Ebenen gleicher Farbe zusammenschieben',
    mergeAdjacent: 'Gleichfarbige Nachbarn verbinden',
    mergeAdjacentTitle:
      'Direkt benachbarte Ebenen gleicher Farbe zu einer machen – beim Umsortieren und Umfärben passiert das von selbst',
    // Isolieren: allein zeigen und zugleich aktivieren, damit Pinsel und
    // Einfügen nicht auf einer unsichtbaren Nachbarebene landen.
    isolate: 'Ebene isolieren',
    isolateOf: '{name} isolieren',
    isolateTitle: 'Nur diese Ebene zeigen und zur aktiven machen',
    exitIsolation: 'Isolation beenden',
    exitIsolationOf: 'Isolation von {name} beenden',
    isolationBanner: 'Isoliert: {name}',
    isolationBannerExit: 'Beenden',
  },

  colorPicker: {
    title: 'Farbe wählen',
    plane: 'Sättigung und Helligkeit',
    hue: 'Farbton',
    hex: 'Hexcode',
  },
  colors: {
    title: 'Farben',
    empty: 'Dieses Dokument hat noch keine Ebenen.',
    use: '{color} verwenden ({layer})',
    swatchTitle: '{layer} — {color}. Doppelklick zum Ersetzen.',
    replace: '{color} ersetzen',
    mergeSimilar: 'Ähnliche zusammenführen',
    threshold: 'Schwelle',
    thresholdLabel: 'Schwelle für Farbähnlichkeit',
    thresholdOff: 'aus',
    thresholdFine: 'fein',
    thresholdMedium: 'mittel',
    thresholdCoarse: 'grob',
    modesLegend: 'Was mit ähnlichen Farben passieren soll',
    modeMerge: 'Ebenen zusammenführen',
    modeRecolor: 'Nur umfärben',
    noneClose: 'Keine zwei Farben liegen so nah beieinander.',
    dragHint: 'Zieh den Regler, um fast gleiche Farben zu einer zusammenzufassen.',
    outcomeMerge: '{before} → {after}',
    outcomeRecolor: '{before} → {after}, Ebenen bleiben',
    layers: { one: '1 Ebene', other: '{count} Ebenen' },
    colours: { one: '1 Farbe', other: '{count} Farben' },
    mergeButton: '{layers} zusammenführen',
    recolorButton: '{layers} umfärben',
    mergedToast: '{removed} in {groups} zusammengeführt.',
    collisionTitle: 'Zwei Ebenen, eine Farbe',
    collisionBody: '„{name}" hat schon {color}. In die bestehende Ebene zusammenführen?',
    collisionConfirm: 'Ebenen zusammenführen',
    collisionKeep: 'Beide behalten',
    recoloredToast: '{changed} umgefärbt; davon bleiben {colours}.',
    foils: { one: '1 Folie', other: '{count} Folien' },
    mapToFoils: 'Auf meine Folien mappen',
    mapToFoilsHint: 'Jede Ebenenfarbe bekommt die nächstliegende Farbe aus deiner eigenen Folienpalette.',
    mapToFoilsUpgrade: 'Auf meine Folien mappen gibt es mit Pro.',
    mapToFoilsEmpty: 'Leg zuerst Folienfarben in deinem Konto an.',
    mapToFoilsEmptyLink: 'Zu meinen Folien',
    mapToFoilsToast: '{layers} auf {foils} gemappt.',
  },

  path: {
    title: 'Pfad',
    empty: 'Wähle einen Pfad aus — oder doppelklicke einen —, um ihn zu vereinfachen oder zu glätten.',
    nodeCount: '{count} Knoten',
    nodeCountPreview: '{before} → {after} Knoten',
    inPaths: ' in {count} Pfaden',
    kept: 'Form behalten — keine Anpassung bleibt innerhalb dieser Toleranz.',
    simplify: 'Vereinfachen',
    simplifyTolerance: 'Toleranz für Vereinfachen',
    smooth: 'Glätten',
    smoothStrength: 'Stärke der Glättung',
    round: 'Ecken runden',
    roundRadius: 'Radius für Eckenrundung',
    roundApply: 'Rundung anwenden',
    roundTooComplex: {
      one: '1 Pfad war zu komplex zum Runden.',
      other: '{count} Pfade waren zu komplex zum Runden.',
    },
  },

  // Dekorkante: der Abschnitt unter „Ecken runden" im Pfad-Panel.
  decoEdge: {
    title: 'Dekorkante',
    kind: 'Art',
    kindLabel: 'Art der Dekorkante',
    kindZigzag: 'Zickzack',
    kindWave: 'Wellen',
    kindScallop: 'Bogen',
    kindStamp: 'Briefmarke',
    amplitude: 'Tiefe',
    amplitudeLabel: 'Tiefe der Dekorkante',
    wavelength: 'Wellenlänge',
    wavelengthLabel: 'Wellenlänge der Dekorkante',
    direction: 'Richtung',
    directionLabel: 'Richtung der Dekorkante',
    directionOutward: 'Nach außen',
    directionInward: 'Nach innen',
    // Die Briefmarke kerbt immer nach innen — der Schalter steht dann fest.
    directionFixed: 'Die Briefmarke kerbt immer nach innen.',
    apply: 'Dekorkante übernehmen',
    empty: 'Wähle einen geschlossenen Pfad aus, um seine Kante zu verzieren.',
    previewOff: 'Zu viele Knoten für eine Vorschau — „Übernehmen" rechnet trotzdem.',
    tooComplex: {
      one: '1 Pfad wurde durch die Dekorkante zu groß.',
      other: '{count} Pfade wurden durch die Dekorkante zu groß.',
    },
    helpTitle: 'Dekorkante',
    helpBody:
      'Verziert die Kante der ausgewählten geschlossenen Pfade mit Zickzack, Wellen, Bögen oder einer Briefmarken-Perforation — durchgehend um alle Ecken herum. Die Wellenlänge wird dabei leicht angepasst, damit auf den Umfang eine ganze Zahl von Perioden passt und das Muster am Startpunkt nahtlos schließt.',
  },

  // Anordnen: Ausrichten, Verteilen, Maße; Lineale, Raster, Hilfslinien, Fang.
  arrange: {
    title: 'Anordnen',
    align: 'Ausrichten',
    reference: 'Bezug',
    referenceSelection: 'Auswahl',
    referencePage: 'Seite',
    left: 'Links ausrichten',
    centerX: 'Horizontal zentrieren',
    right: 'Rechts ausrichten',
    top: 'Oben ausrichten',
    centerY: 'Vertikal zentrieren',
    bottom: 'Unten ausrichten',
    order: 'Reihenfolge',
    toFront: 'Ganz nach vorn',
    forward: 'Eine Stufe nach vorn',
    backward: 'Eine Stufe nach hinten',
    toBack: 'Ganz nach hinten',
    distribute: 'Verteilen',
    distributeX: 'Mitten horizontal gleichmäßig verteilen',
    distributeY: 'Mitten vertikal gleichmäßig verteilen',
    gapsX: 'Gleiche Lücken horizontal',
    gapsY: 'Gleiche Lücken vertikal',
    empty: 'Wähle einen Pfad aus, um ihn auszurichten oder zu bemaßen.',
    hint: 'An der Auswahl ausrichten geht ab zwei Pfaden, Verteilen ab drei — an der Seite schon ab einem.',
    measures: 'Maße',
    x: 'X (mm)',
    y: 'Y (mm)',
    width: 'Breite (mm)',
    height: 'Höhe (mm)',
    lockAspect: 'Seitenverhältnis sperren',
    rotation: 'Drehung (°)',
    rotate: 'Drehen',
    rotationHint: 'Dreht um den Bezugspunkt und setzt das Feld wieder auf 0.',
    anchor: 'Bezugspunkt',
    anchors: {
      nw: 'Oben links',
      n: 'Oben Mitte',
      ne: 'Oben rechts',
      w: 'Mitte links',
      c: 'Mitte',
      e: 'Mitte rechts',
      sw: 'Unten links',
      s: 'Unten Mitte',
      se: 'Unten rechts',
    },
    invalidNumber: 'Bitte eine Zahl eingeben, zum Beispiel 50,5.',
    arrayTitle: 'Vervielfältigen',
    arrayCircle: 'Im Kreis',
    arrayGrid: 'Im Raster',
    arrayCount: 'Anzahl',
    arrayRows: 'Zeilen',
    arrayCols: 'Spalten',
    arrayGapMm: 'Abstand (mm)',
    arrayRadiusMm: 'Radius (mm)',
    arrayRotate: 'Kopien mitdrehen',
    arrayEmpty: 'Wähle einen Pfad aus, um ihn zu vervielfältigen.',
    arrayTooMany: 'Das würde eine Ebene über {max} Pfade bringen — leg weniger Kopien an.',
    view: 'Lineale, Raster, Fang',
    rulers: 'Lineale',
    grid: 'Raster',
    gridStep: 'Rasterweite',
    gridStepValue: '{step} mm',
    snap: 'Fang',
    snapHint: 'Fängt an Raster, Hilfslinien, Seitenkanten und -mitte sowie an anderen Pfaden. Alt beim Ziehen schaltet den Fang aus.',
    guides: 'Hilfslinien',
    guideAxis: 'Richtung',
    guideVertical: 'Senkrecht',
    guideHorizontal: 'Waagerecht',
    guideAt: 'Hilfslinie bei (mm)',
    addGuide: 'Hilfslinie anlegen',
    noGuides: 'Noch keine Hilfslinien — ziehe eine aus dem Lineal oder lege sie hier an.',
    guideRow: '{axis} bei {mm}',
    guideSelect: 'Hilfslinie {axis} bei {mm} auswählen',
    removeGuide: 'Hilfslinie {axis} bei {mm} löschen',
    rulerTop: 'Lineal oben, Millimeter',
    rulerLeft: 'Lineal links, Millimeter',
    rulerUnit: 'mm',
  },

  // Bogen-Layout: Motive per Bin-Packing auf eine Matte legen (nesting.ts,
  // panels/SheetPanel.tsx).
  nesting: {
    title: 'Bogen',
    format: 'Mattengröße',
    formats: {
      'cricut-12x12': 'Cricut-Matte 30 × 30 cm',
      'cricut-12x24': 'Cricut-Matte 30 × 60 cm',
      'silhouette-12x12': 'Silhouette-Matte 30 × 30 cm',
      'silhouette-12x24': 'Silhouette-Matte 30 × 60 cm',
      'brother-12x12': 'Brother-Matte 30 × 30 cm',
      'brother-12x24': 'Brother-Matte 30 × 60 cm',
      a4: 'A4 (210 × 297 mm)',
      a3: 'A3 (297 × 420 mm)',
      custom: 'Eigene Größe',
    },
    width: 'Breite (mm)',
    height: 'Höhe (mm)',
    margin: 'Rand (mm)',
    spacing: 'Abstand (mm)',
    rotate: 'Drehen erlauben (90°)',
    arrange: 'Anordnen',
    arrangeHint: 'Packt die Auswahl — ohne Auswahl alle sichtbaren Pfade — auf die Matte und setzt die Seite auf Mattengröße.',
    copies: 'Kopien',
    fillMax: 'So viele wie passen',
    fill: 'Kopien füllen',
    fillHint: 'Dupliziert das ausgewählte Motiv und packt alle Kopien mit auf die Matte.',
    needSelection: 'Wähle ein Motiv aus, um es zu vervielfältigen.',
    invalidNumber: 'Bitte eine Zahl eingeben, zum Beispiel 50,5.',
    invalidSize: 'Breite und Höhe müssen zwischen {min} und {max} mm liegen.',
    overflow: {
      one: '1 Teil passt nicht mehr auf die Matte und liegt rechts daneben.',
      other: '{count} Teile passen nicht mehr auf die Matte und liegen rechts daneben.',
    },
    nothingFits: 'Nicht einmal eine Kopie passt auf die Matte — wähle eine größere Matte.',
    layerLimit: 'Eine Ebene fasst höchstens {max} Pfade — es werden {count} Kopien.',
    empty: 'Keine sichtbaren Pfade — es gibt nichts anzuordnen.',
    byColor: 'Nach Farben ordnen',
    byColorHint: 'Gruppiert alle sichtbaren Pfade nach der Farbe ihrer Ebene und legt jede Farbe in ein eigenes Band untereinander.',
    byColorBand: {
      one: '{color}: 1 Teil, {range}',
      other: '{color}: {count} Teile, {range}',
    },
    trueShape: 'Konturgenau (Pro)',
    trueShapeHint: 'Packt Silhouetten statt Rechtecke — Teile dürfen ineinandergreifen. Rechnet einen Moment länger.',
    trueShapePro: 'Konturgenaues Anordnen ist eine Pro-Funktion.',
    trueShapeProgress: 'Ordne konturgenau an … {done}/{total}',
    trueShapeFailed: 'Konturgenaues Anordnen ist fehlgeschlagen.',
    trueShapeChanged: 'Das Dokument hat sich währenddessen geändert — das Ergebnis wurde verworfen.',
    quality: 'Genutzte Fläche: {after} % konturgenau statt {before} % mit Rechtecken.',
  },

  // Entgitterhilfen im Bogen-Panel: Rahmen und Stege als eigene Linien-Ebene.
  weeding: {
    title: 'Entgittern',
    margin: 'Abstand (mm)',
    bridges: 'Trennstege',
    run: 'Hilfen anlegen',
    layerName: 'Entgittern',
    hint: 'Rahmen und Stege landen als eigene Linien-Ebene ganz oben — fürs Entgittern von Folie, nach dem Schneiden einfach löschen.',
    empty: 'Keine sichtbaren Pfade — es gibt nichts zu entgittern.',
    done: { one: '1 Entgitterhilfe angelegt.', other: '{count} Entgitterhilfen angelegt.' },
  },

  // Serienfertigung mit Variablen (mailMerge.ts, MailMergeDialog.tsx):
  // Platzhalter im Text-Rezept, eine Liste, ein Exemplar je Zeile.
  mailMerge: {
    menu: 'Serie…',
    title: 'Serie anlegen',
    helpTitle: 'Serie',
    help: 'Schreibe einen Platzhalter in geschweiften Klammern in einen Text, zum Beispiel „Hallo {Name}". Trage darunter eine Liste ein — eine Zeile je Exemplar — und die Serie legt für jede Zeile eine Kopie des Designs an, mit eingesetztem Text, als angeheftete Objektgruppe auf dem Bogen.',
    noPlaceholders:
      'Kein Platzhalter im Dokument. Setze einen Text mit geschweiften Klammern, zum Beispiel „Hallo {Name}" — die Serie ersetzt ihn dann Zeile für Zeile.',
    placeholders: 'Gefundene Platzhalter',
    list: 'Liste',
    listHintOne: 'Eine Zeile je Exemplar.',
    listHintMany: 'Eine Zeile je Exemplar, Spalten mit Semikolon, Komma oder Tabulator getrennt.',
    listExampleOne: 'Anna\nBo\nCem',
    listExampleMany: 'Anna;3\nBo;7',
    count: { one: '1 Exemplar', other: '{count} Exemplare' },
    headerFound: 'Erste Zeile als Kopfzeile gelesen: {columns}.',
    scope: 'Umfang',
    scopeAll: 'Ganzes Design',
    scopeText: 'Nur Text-Ebenen mit Platzhaltern',
    spacing: 'Abstand (mm)',
    keepTemplate: 'Vorlage behalten',
    fits: '{fit} von {total} passen auf den Bogen — der Rest liegt rechts daneben.',
    tooMany: 'Höchstens {max} Exemplare auf einmal — die weiteren Zeilen bleiben liegen.',
    layerLimit: 'Eine Ebene fasst höchstens {max} Pfade — es werden {count} Exemplare.',
    empty: 'Trage mindestens eine Zeile ein.',
    nothingToPlace: 'Nichts anzuordnen — im Umfang liegt kein einziger sichtbarer Pfad.',
    create: 'Serie anlegen',
    groupName: 'Serie',
    done: { one: '1 Exemplar angelegt.', other: '{count} Exemplare angelegt.' },
    overflow: {
      one: '1 Exemplar passt nicht mehr auf den Bogen und liegt rechts daneben.',
      other: '{count} Exemplare passen nicht mehr auf den Bogen und liegen rechts daneben.',
    },
    failed: 'Die Serie konnte nicht angelegt werden.',
    counterHelp:
      'Soll mitgezählt werden, schreibe {#} in den Text — zum Beispiel „Tisch {#}". Das zählt die Serie selbst hoch; eine Liste braucht dafür keine Spalte, und beides zusammen geht auch („{Name}, Tisch {#}").',
    counter: 'Zähler',
    counterStart: 'Start',
    counterStep: 'Schrittweite',
    counterDigits: 'Stellen (mit führenden Nullen)',
    counterPrefix: 'Davor',
    counterSuffix: 'Danach',
    counterPreview: 'Ergibt „{first}" bis „{last}".',
    counterPreviewOne: 'Ergibt „{first}".',
    counterZeroStep: 'Schrittweite 0 — dann tragen alle Exemplare dieselbe Nummer.',
    copies: 'Anzahl',
    copiesHint: 'Ohne Liste zählt nur der Zähler: so viele Exemplare werden angelegt, höchstens {max}.',
    tooManyCopies: 'Höchstens {max} Exemplare auf einmal — es werden {max} angelegt.',
  },

  cleanup: {
    title: 'Aufräumen',
    parts: { one: '1 Teil', other: '{count} Teile' },
    smallerThan: '{parts} kleiner als {size}',
    smallParts: 'Kleinteile',
    smallPartSize: 'Größe der Kleinteile',
    // Ein Knopf je Gerät setzt den Regler direkt auf die Schwelle, die der
    // Export für dieses Gerät ohnehin schon meldet (device_export/profiles).
    devicePresets: 'Geräte-Voreinstellungen',
    devicePreset: 'Voreinstellung {name}',
    deviceCricut: 'Cricut',
    deviceXtool: 'xTool',
    deviceBrother: 'Brother',
    remove: '{parts} entfernen',
    totalNodes: 'Knoten gesamt: {count}',
    optimize: 'Für Cricut optimieren (≤ 5 000 Knoten)',
    optimizedToast: '{before} → {after} Knoten bei {tolerance}',
    optimizeFailed: '5 000 Knoten nicht erreicht; bestmögliches Ergebnis angewendet.',
    overlaps: 'Überlappungen',
    overlapsHint: 'Formen, die sich überlappen, werden zweimal geschnitten. Verschmelzen lässt eine Kontur je Ebene übrig.',
    weld: 'Überlappende Formen verschmelzen',
    weldTitle: 'Verschmilzt jede Ebene auf der Zeichenfläche — solange eine Ebene solo ist, nur diese. Das Häkchen im Export verschmilzt immer jede sichtbare Ebene.',
    // Der Plotter-Doktor auf dem offenen Dokument — dieselben Befunde und
    // Reparaturen wie beim SVG-Import (svgDoctor.ts).
    doctorClean: 'Keine Auffälligkeiten — das Dokument ist schnittklar.',
    doctorRepair: 'Alles beheben',
    doctorDone: { one: 'Befund behoben — 1 Änderung.', other: 'Befund behoben — {count} Änderungen.' },
    // Der Schablonen-Check: Teile, die beim Schneiden einer Schablone
    // herausfallen würden (ein Loch ohne Steg zur Umgebung). Bewusst ohne
    // Auto-Reparatur — ob ein Steg dahin gehört, ist Gestaltung, kein Fehler.
    stencilTitle: 'Schablonen-Check',
    stencilNone: 'Keine losen Innenteile',
    stencilFound: { one: '{count} loses Innenteil', other: '{count} lose Innenteile' },
    stencilSkipped: 'Zu viele Pfade für den Schablonen-Check',
    stencilShow: 'Anzeigen',
    stencilHide: 'Ausblenden',
    // Die Reparatur zum Check: Stege, die jede Insel mit dem umgebenden
    // Material verbinden (geometry/stencilBridges.ts) — Vorschau als
    // Overlay, Anwenden als ein Undo-Schritt.
    bridgeWidth: 'Steg-Breite (mm)',
    bridgeShow: 'Stege zeigen',
    bridgeHide: 'Stege ausblenden',
    bridgeInsert: 'Stege einfügen',
    bridgeNone: 'Kein Platz für Stege gefunden — bitte von Hand setzen.',
    bridgeDone: { one: '1 Steg eingefügt — keine losen Teile mehr.', other: '{count} Stege eingefügt — keine losen Teile mehr.' },
    bridgeLeft: { one: 'Stege eingefügt, aber 1 Teil bleibt lose.', other: 'Stege eingefügt, aber {count} Teile bleiben lose.' },
  },

  shape: {
    title: 'Form',
    boolean: 'Boolesch',
    booleanHint: 'Wähle mindestens zwei Pfade. Die zuerst gewählte Form ist das Ziel, die übrigen wirken auf sie.',
    unite: 'Vereinen',
    subtract: 'Abziehen',
    intersect: 'Schneiden',
    exclude: 'Ausschließen',
    divide: 'Teilen',
    uniteTitle: 'Alle gewählten Formen zu einer Fläche',
    subtractTitle: 'Zieht die übrigen Formen von der zuerst gewählten ab',
    intersectTitle: 'Nur die Fläche, die alle gewählten Formen gemeinsam haben',
    excludeTitle: 'Alles außer der Überlappung',
    divideTitle: 'Zerlegt die Formen entlang ihrer Überlappungen in einzelne Stücke',
    booleanEmpty: 'Ergebnis leer — die Formen überlappen sich nicht so, dass etwas übrig bleibt.',
    booleanOpen: 'Offene Pfade lassen sich nicht verrechnen. Schließe sie erst im Pfad-Editor.',
    booleanDone: { one: 'Fertig — 1 Pfad', other: 'Fertig — {count} Pfade' },
    offset: 'Kontur / Offset',
    offsetEmpty: 'Wähle einen oder mehrere geschlossene Pfade, um eine Kontur zu erzeugen.',
    distance: 'Abstand',
    distanceLabel: 'Abstand der Kontur in Millimetern',
    distanceInput: 'Abstand als Zahl in Millimetern',
    join: 'Ecken',
    joinRound: 'Rund',
    joinSquare: 'Eckig',
    joinMiter: 'Spitz',
    outlineOnly: 'Nur Außenkontur (Löcher verwerfen)',
    target: 'Ergebnis',
    targetLayer: 'Als neue Ebene „{name}"',
    targetReplace: 'Pfad ersetzen',
    layerColor: 'Farbe der Kontur-Ebene',
    layerName: 'Kontur',
    apply: 'Kontur anwenden',
    previewOff: 'Vorschau aus — die Auswahl hat über {limit} Knoten. „Anwenden" rechnet trotzdem.',
    offsetEmptyResult: 'Nichts übrig — der Abstand ist größer als die Form.',
    offsetDone: 'Kontur erzeugt',
    replaced: { one: '1 Pfad ersetzt', other: '{count} Pfade ersetzt' },
    textHeading: 'Text',
    weldText: 'Buchstaben verschmelzen',
    weldTextTitle: 'Vereint alle Buchstaben der Text-Ebene zu einem Pfad — Löcher bleiben erhalten.',
    frameBorder: 'Rahmenbreite',
    warp: 'Verzerren',
    warpUpgrade: 'Bogen, Perspektive und konisches Verzerren (Becher) gibt es mit Pro.',
    warpEmpty: 'Wähle einen oder mehrere Pfade, um sie zu verzerren.',
    warpMode: 'Modus',
    warpModeArch: 'Bogen',
    warpModePerspective: 'Perspektive',
    warpModeConical: 'Konisch (Becher)',
    warpBend: 'Biegung',
    warpBendLabel: 'Biegung in Prozent — positiv nach oben, negativ nach unten',
    warpTop: 'Oben schmaler',
    warpTopLabel: 'Obere Kante in Prozent schmaler; negativ macht sie breiter',
    warpBottom: 'Unten schmaler',
    warpBottomLabel: 'Untere Kante in Prozent schmaler; negativ macht sie breiter',
    warpTopDiameter: 'Ø oben (mm)',
    warpBottomDiameter: 'Ø unten (mm)',
    warpHeight: 'Höhe (mm)',
    warpConicalHint:
      'Bildet die Auswahl auf die abgewickelte Becherwand ab — die Oberkante der Auswahl liegt am oberen Rand.',
    warpPreset: 'Becher-Vorlage',
    warpPresetCustom: 'Eigene Maße',
    warpPresetSkinny20: '20-oz-Tumbler (skinny), ca. 71/67/206',
    warpPresetTumbler30: '30-oz-Tumbler, ca. 88/73/200',
    warpPresetLatte17: 'Latte-Becher (17 oz), ca. 94/62/118',
    warpSmooth: 'Ergebnis mit Kurven glätten',
    warpApply: 'Verzerren anwenden',
    warpDone: { one: '1 Pfad verzerrt', other: '{count} Pfade verzerrt' },
    warpNoFit:
      'Das Motiv passt nicht auf diesen Becher — breiter als der obere Umfang oder höher als die abgewickelte Wand.',
    knifeDone: { one: 'Geschnitten — 1 Pfad', other: 'Geschnitten — {count} Pfade' },
    knifeEmpty: 'Die Linie schneidet keine geschlossene Form ganz durch.',
    knifeHint: 'Wähle eine geschlossene Form, oder ziehe die Linie über eine.',
  },

  // Überfüllen und Ausstanzen für geschichtete Folie: geometry/layering.ts,
  // LayeringPanel.tsx.
  layering: {
    title: 'Schichtung',
    hint: 'Braucht mindestens zwei sichtbare Gravur-Ebenen, die einander überdecken.',
    overlap: 'Überlappung',
    overlapLabel: 'Überlappung in Millimetern',
    overlapInput: 'Überlappung als Zahl in Millimetern',
    scope: 'Umfang',
    scopeActive: 'Nur aktive Ebene (als Unterlage)',
    scopeAll: 'Alle Ebenen',
    knockout: 'Ausstanzen',
    knockoutTitle:
      'Nimmt aus jeder Ebene heraus, was die Ebenen darüber decken — bis auf einen schmalen Saum am Rand.',
    trap: 'Überfüllen',
    trapTitle: 'Lässt jede Ebene um die Überlappung unter die Ebenen darüber wachsen.',
    done: { one: '1 Pfad geändert', other: '{count} Pfade geändert' },
    none: 'Keine Überlappung gefunden',
    previewOff: 'Vorschau aus — die Ebenen haben über {limit} Knoten. Die Aktionen rechnen trotzdem.',
  },

  // Der Strass-Generator (Pro): geometry/rhinestone.ts, RhinestonePanel.tsx.
  rhinestone: {
    title: 'Strass',
    upgrade: 'Der Strass-Generator ist eine Pro-Funktion.',
    hint: 'Wähle einen oder mehrere Pfade, um ein Strass-Muster zu erzeugen.',
    size: 'Steingröße',
    sizeLabel: 'Steingröße als SS-Maß',
    sizeCustom: 'Eigene…',
    customSize: 'Steindurchmesser',
    customSizeLabel: 'Steindurchmesser in Millimetern',
    holeNote: 'Lochdurchmesser {hole} — Stein plus {clearance} Spiel, damit er von selbst ins Loch fällt.',
    spacing: 'Mindestabstand',
    spacingLabel: 'Mindestabstand zwischen den Löchern in Millimetern',
    mode: 'Modus',
    modeContour: 'Kontur',
    modeFill: 'Füllung',
    modeBoth: 'Beides',
    grid: 'Gitter',
    gridHex: 'Versetzt',
    gridSquare: 'Quadratisch',
    hideSource: 'Quell-Ebenen ausblenden',
    count: { one: '1 Stein', other: '{count} Steine' },
    truncated: 'Abgebrochen bei {limit} Steinen — größerer Stein oder mehr Abstand hilft.',
    layerName: 'Strass',
    apply: 'Strass anwenden',
    done: { one: 'Strass-Ebene erzeugt — 1 Stein', other: 'Strass-Ebene erzeugt — {count} Steine' },
    empty: 'Kein Stein passt — die Form ist zu klein für diese Steingröße.',
  },

  // Die Zeichenwerkzeuge: Formen-Gruppe in der Werkzeugleiste, Stift, Bleistift.
  shapes: {
    menu: 'Formen',
    title: 'Formen und Stift',
    layerName: 'Formen',
    options: 'Optionen',
    sides: 'Ecken (3–12)',
    innerRadius: 'Innenradius (%)',
    innerRadiusHint: 'Bei 100 % ein regelmäßiges Vieleck, darunter ein Stern.',
    cornerRadius: 'Eckenradius (mm)',
    modifierHint: 'Umschalt hält das Seitenverhältnis (Quadrat, Kreis, 45°-Linie), Alt zieht von der Mitte auf.',
    penHint: 'Klick setzt eine Ecke, Ziehen zieht Griffe heraus. Klick auf den Startpunkt schließt, Eingabe oder Esc beendet offen, Rücktaste nimmt den letzten Punkt zurück.',
  },

  weld: {
    nothing: 'Nichts zu verschmelzen',
    layers: { one: '1 Ebene verschmolzen', other: '{count} Ebenen verschmolzen' },
  },

  invert: {
    nothing: 'Nichts zu invertieren — die Ebene hat keine geschlossene Fläche',
    layers: { one: '1 Ebene invertiert', other: '{count} Ebenen invertiert' },
  },

  raster: {
    title: 'Bitmap',
    brush: 'Pinsel',
    brushSize: 'Pinselgröße',
    brushHint: 'Der Pinsel (P) malt in der aktiven Farbe, der Radierer (E) malt weiß. [ und ] ändern die Größe.',
    brushHintLayer:
      'Der Pinsel (P) malt Formen auf die aktive Ebene, der Radierer (E) radiert sie an. [ und ] ändern die Größe.',
    target: 'Malen auf',
    targetLabel: 'Ziel von Pinsel und Radierer',
    targetLayer: 'Ebene',
    targetLayerHint: 'Striche werden Formen auf der aktiven Ebene',
    targetPhoto: 'Foto',
    targetPhotoHint: 'Striche ändern das Quellbild fürs Neu-Vektorisieren',
    targetPhotoMissing: 'Dieses Projekt hat kein Quellbild',
    smartFill: 'Smarte Füllung',
    smartFillHint: 'Die smarte Füllung (F) nimmt die Fläche des Bildes unter dem Zeiger und vektorisiert sie in die Ebene der aktiven Farbe. Die Toleranz sagt, wie weit die Farbe eines Pixels von der angeklickten abweichen darf; „Überall" nimmt diese Farbe auf dem ganzen Bild, nicht nur, wo sie zusammenhängt.',
    dragRectangle: 'Zieh ein Rechteck auf der Zeichenfläche auf.',
    cropSize: '{width} × {height} mm',
    crop: 'Zuschneiden',
    cancel: 'Abbrechen',
    // Was der Undo-Eintrag eines Pinselstrichs heißt.
    strokeBrush: 'Pinsel',
    strokeErase: 'Radieren',
    // Die Ebene, die ein Pinselstrich auf einem leeren Dokument anlegt.
    layerName: 'Malen',
  },

  autofill: {
    tolerance: 'Toleranz',
    toleranceLabel: 'Toleranz der Füllung',
    modeLabel: 'Füllmodus',
    contiguous: 'Zusammenhängend',
    contiguousHint: 'Nur die Fläche, die mit dem angeklickten Punkt zusammenhängt',
    global: 'Überall',
    globalHint: 'Jede Fläche dieser Farbe auf dem ganzen Bild',
    tooLargeGlobal: 'Diese Farbe ist zu detailliert, um sie in einem Zug zu füllen. Versuch eine kleinere Toleranz oder wechsle auf „Zusammenhängend".',
    tooLarge: 'Diese Fläche ist zu detailliert, um sie in einem Zug zu füllen. Versuch eine kleinere Toleranz.',
  },

  reference: {
    title: 'Referenz',
    originalImage: 'Originalbild',
    opacity: 'Deckkraft des Originalbilds',
    mockupsTitle: 'Produkt-Mockups',
    mockupBecher: 'Becher',
    mockupTshirt: 'T-Shirt',
    mockupKlappkarte: 'Klappkarte',
    mockupGlas: 'Glas',
    mockupHint: 'Passt zu einem Dokument von {width} × {height}.',
    mockupRemove: 'Entfernen',
  },

  retrace: {
    title: 'Neu vektorisieren',
    confirmTitle: 'Bild neu vektorisieren',
    confirmBody: 'Ersetzt alle Vektor-Ebenen. Deine Pfad-Änderungen gehen verloren.',
    confirm: 'Neu vektorisieren',
    button: 'Neu vektorisieren',
    running: 'Vektorisiert…',
    noImage: 'Dieses Projekt hat kein Ausgangsbild — es wurde aus einer Vorlage geöffnet oder leer angelegt. Lade ein Bild hoch, um es zu vektorisieren.',
    uploadFailed: 'Das bearbeitete Bild konnte nicht hochgeladen werden. Bitte versuch es noch einmal.',
    started: 'Das Bild wird neu vektorisiert…',
    limitReached: 'Tageslimit erreicht ({used} / {limit}).',
    rejected: 'Der Server hat diese Einstellungen nicht angenommen.',
    failed: 'Die Vektorisierung konnte nicht gestartet werden. Bitte versuch es noch einmal.',
    failedToast: 'Die Vektorisierung konnte nicht gestartet werden.',
    unlimited: 'für unbegrenzte Vektorisierungen.',
    // Die Vorschau zu den Reglern: läuft auf 1024 px, kostet keinen Tagesslot.
    preview: 'Vorschau',
    previewHint: 'Zeigt, was die Regler ergeben – kostet keinen Tagesslot.',
    previewPending: 'Vorschau wird berechnet…',
    previewSummary: 'Vorschau: {px} px, {colors}, {seconds} s',
    previewColors: { one: '1 Farbe', other: '{count} Farben' },
    previewShowOriginal: 'Original zeigen',
    previewShowPreview: 'Vorschau zeigen',
    previewTimeout:
      'Die Vorschau hat zu lange gedauert. Die Regler bleiben bedienbar – „Neu vektorisieren“ läuft ohne dieses Limit.',
    previewRejected: 'Der Server hat diese Einstellungen nicht angenommen.',
    previewThrottled: 'Zu viele Vorschauen in kurzer Zeit. Versuch es in ein paar Minuten noch einmal.',
    previewFailed: 'Die Vorschau konnte nicht berechnet werden.',
    previewDirty: 'Die Vorschau zeigt das zuletzt hochgeladene Bild – nicht hochgeladene Pinselstriche fehlen.',
    // Der Farbanzahl-Vergleich: drei Vorschauen bei halber, aktueller und
    // doppelter Palettengröße, nebeneinander zum Anklicken.
    compareTitle: 'Farben vergleichen',
    compareRun: 'Drei Varianten rechnen',
    compareUse: '{count} Farben übernehmen',
    compareTileLabel: '{colors} · {layers}',
    compareLayers: { one: '1 Ebene', other: '{count} Ebenen' },
    compareBusy: 'Gerade läuft schon eine andere Vorschau. Versuch es in ein paar Sekunden noch einmal.',
    compareFailed: 'Der Farbvergleich konnte nicht berechnet werden. Bitte versuch es noch einmal.',
    // Die Bereichs-Nachbesserung (Task 8): ein Rechteck über dem Bild, allein
    // dieser Ausschnitt geht noch einmal durch die Vektorisierung.
    regionTitle: 'Bereich nachbessern',
    regionStart: 'Bereich wählen',
    regionRun: 'Bereich vektorisieren',
    regionReplace: 'Pfade im Bereich ersetzen',
    regionHint:
      'Zieh auf der Leinwand ein Rechteck über die Stelle, die besser werden soll – mindestens 10 mm breit. Der Lauf kostet einen Tagesslot wie eine Vektorisierung.',
    regionDone: 'Der Bereich wurde neu vektorisiert.',
    regionFailed: 'Der Bereich konnte nicht neu vektorisiert werden. Bitte versuch es noch einmal.',
    regionOffline: 'Ohne Verbindung lässt sich kein Bereich vektorisieren.',
    // Benannte Regler-Sätze, persönlich je Konto (Task 9, Runde 3). Anwenden
    // setzt nur die Felder, Speichern hält den aktuellen Stand fest -- keins
    // von beidem löst für sich eine Vektorisierung aus.
    presetsTitle: 'Presets',
    presetsSelectLabel: 'Preset anwenden',
    presetsSelectEmpty: 'Kein Preset gespeichert',
    presetsSelectPlaceholder: 'Preset wählen…',
    presetsSave: 'Als Preset speichern',
    presetsSaveTitle: 'Preset speichern',
    presetsSaveLabel: 'Name',
    presetsSaved: 'Preset „{name}“ gespeichert.',
    presetsSaveFailed: 'Das Preset konnte nicht gespeichert werden.',
    presetsDuplicateName: 'Ein Preset mit diesem Namen gibt es schon.',
    presetsDelete: 'Preset löschen',
    presetsDeleteTitle: 'Preset löschen?',
    presetsDeleteBody: 'Wird endgültig gelöscht.',
    presetsDeleted: 'Preset „{name}“ gelöscht.',
    presetsDeleteFailed: 'Das Preset konnte nicht gelöscht werden.',
    // Task 1 „Legacy Cleanup": ein per Hand-API gespeichertes Preset kann eine
    // Engine tragen, die dieser Server (mehr) nicht hat -- `applyTracePreset`
    // tauscht sie beim Anwenden gegen die erste verfügbare, und dieser Toast
    // sagt, was gerade passiert ist, statt den nächsten Retrace stillschweigend
    // mit einem 422 enden zu lassen.
    presetEngineDegraded: 'Engine „{from}“ ist hier nicht verfügbar — „{to}“ wird verwendet.',
  },

  traceParams: {
    colors: 'Farben',
    filterSpeckle: 'Sprenkel filtern',
    minArea: 'Teile verwerfen, die kleiner sind als',
    paletteSize: 'Palette auf',
    paletteSizeUnit: 'Farben begrenzen',
    paletteSizeOff: 'aus',
    paletteSizeAria: 'Palette auf so viele Farben begrenzen (leer = aus)',
    mergeDeltaE: 'Farben zusammenlegen, die näher als ΔE',
    mergeDeltaEUnit: 'liegen',
    mergeDeltaEAria: 'Farben zusammenlegen, die näher als dieser ΔE-Wert liegen',
    // Der einzige Regler, den potrace, autotrace und skeleton wirklich lesen
    // (I4, Runde 4 Folge-Aufgaben): ab welchem Helligkeitsanteil ein Pixel
    // als Zeichnung statt als Papier zählt. vtracer binarisiert nie und
    // bekommt diesen Regler deshalb nicht zu sehen.
    threshold: 'Schwellwert',
    thresholdAria: 'Schwellwert, ab dem ein Pixel als Zeichnung statt als Papier gilt',
    mode: 'Modus',
    hierarchy: 'Hierarchie',
    // Ebenentrennung (Task 8, round three): nach Farbe wie bisher, oder nach
    // Helligkeit -- das Bild wird dafür vor der Quantisierung grau.
    separate: 'Ebenen trennen',
    separateColor: 'Nach Farbe',
    separateBrightness: 'Nach Helligkeit',
    engine: 'Engine',
    // Autotrace' `-centerline`-Modus: eine Bleistiftskizze wird an ihrer
    // Mittellinie geschnitten statt als gefüllte Fläche (Task 7). Nur in der
    // Liste, wenn der Server das Binary gefunden hat (`engines` in
    // GET /api/v1/entitlements).
    engineAutotrace: 'Mittellinie (Skizze)',
    // Dieselbe Idee ohne fremdes Programm (Runde 4, Task 5): ImageMagick
    // dünnt die Zeichnung auf einen Pixel, daraus werden gerade Linienzüge.
    // „einfach" ist die Warnung: keine Kurven, sondern Polygonzüge -- dafür
    // steht die Engine überall zur Verfügung.
    engineSkeleton: 'Mittellinie (einfach)',
  },

  export: {
    title: 'Export',
    deviceProfile: 'Geräteprofil',
    profileCricut: 'Cricut',
    profileXtool: 'xTool-Laser',
    profileBrother: 'Brother ScanNCut',
    format: 'Format',
    proSuffix: ' — Pro',
    proFormats: 'DXF, PNG, PDF, Brother FCM und xTool XCS sind Pro-Formate.',
    mat: 'Matte',
    mat12x12: '12 × 12 Zoll (Standardmatte)',
    mat12x24: '12 × 24 Zoll (lange Matte)',
    fcmHint: 'Die FCM-Datei liest der ScanNCut direkt vom USB-Stick; die Zeichnung liegt mittig auf der Matte, jede Ebene ist ein eigenes Objekt. Noch ohne Gerät getestet — melde dich, wenn dein ScanNCut die Datei nicht liest.',
    brotherHint: 'Für CanvasWorkspace als SVG in Millimetern, für den ScanNCut direkt als Brother FCM.',
    xcsHint:
      'Die XCS-Datei öffnet xTool Studio direkt als Projekt: jede Ebene ist ein Objekt, Schnittebenen stehen auf „Schneiden“, Gravurebenen auf „Gravieren (Füllung)“ — Material, Leistung und Geschwindigkeit stellst du in Studio ein. Angelegt für den xTool S1; ein anderes xTool wählst du beim Öffnen.',
    silhouetteHint: 'Silhouette Studio: die kostenlose Basisversion liest DXF, SVG erst ab der Designer Edition. Einen .studio3-Export gibt es nicht — das Format ist verschlüsselt.',
    width: 'Breite (mm)',
    height: 'Höhe (mm)',
    weldFirst: 'Überlappungen vor dem Export verschmelzen',
    // „Für Bügelfolie spiegeln" (HTV): der Klassiker unter den
    // Anfängerfehlern -- Bügelfolie wird spiegelverkehrt geschnitten, Cricut
    // und Silhouette fragen an genau dieser Stelle aktiv nach. Gespiegelt
    // wird nur der Export (`DeviceExport::Mirror`); die Arbeitsfläche im
    // Editor bleibt unangetastet, deshalb sagt der Hilfetext beides.
    mirrorHtv: 'Für Bügelfolie spiegeln',
    mirrorHtvHint: 'Das Motiv wird horizontal gespiegelt exportiert — die Arbeitsfläche im Editor bleibt, wie sie ist. Bügelfolie (HTV) schneidet man spiegelverkehrt; nach dem Aufbügeln stimmt es wieder.',
    // Der Verwechslungsschutz nach dem Export: die Datei, die gerade das
    // Haus verlassen hat, ist gespiegelt — nicht mit einer normalen
    // Schnittdatei zu verwechseln.
    mirroredBadge: 'Gespiegelt exportiert — bereit für Bügelfolie.',
    button: 'Exportieren',
    exporting: 'Exportiert…',
    // Reicht seit Task 8 das gewählte Geräteprofil durch, statt Cricut fest
    // zu nennen -- der Knopf braucht deshalb keinen Profilnamen mehr.
    layersZip: 'ZIP je Ebene',
    // M13-Rest: eigenes Busy-Label wie bei der Rollentrennung und "Alles als
    // ZIP" -- dieser Knopf war der einzige der vier ZIP-/Export-Nachbarn ohne
    // eines, obwohl er während des Exports genauso disabled ist.
    layersZipExporting: 'Wird gepackt…',
    layersZipFailed: 'Das ZIP je Ebene konnte nicht heruntergeladen werden.',
    // xTool-Rollentrennung (Task 7): Gravur und Schnitt als getrennte
    // Dateien, nur sichtbar, wenn xTool das gewählte Geräteprofil ist.
    roleSplitZip: 'Gravur & Schnitt getrennt (ZIP)',
    // M2: eigenes Busy-Label -- sonst heißt dieser Knopf im Ladezustand wie
    // die drei anderen Export-Knöpfe.
    roleSplitZipExporting: 'Wird getrennt…',
    roleSplitProFeature: 'Gravur & Schnitt getrennt ist eine Pro-Funktion.',
    // "Alles als ZIP" (Task 8): svg, dxf, png und pdf für das gewählte
    // Geräteprofil, dazu fcm für Brother und xcs für xTool -- eine Datei je
    // sinnvollem Format.
    bundleZip: 'Alles als ZIP',
    // M2: eigenes Busy-Label, aus demselben Grund wie bei der Rollentrennung.
    bundleZipExporting: 'Wird gepackt…',
    bundleProFeature: 'Alles als ZIP ist eine Pro-Funktion.',
    // Das Shop-Paket (Etsy-Runde): Schnittdateien, vier Produktvorschauen und
    // ein Textentwurf -- was eine Verkäuferin in ihren Shop lädt.
    shopKit: 'Shop-Paket als ZIP',
    shopKitExporting: 'Wird gepackt…',
    shopKitHint:
      'Schnittdateien, vier Produktvorschauen (Becher, T-Shirt, Klappkarte, Glas), ein Textentwurf für die Artikelbeschreibung sowie eine Produkt-CSV für Shopify und ein ausgefülltes Etsy-Formular.',
    // Was das Paket bewusst offen lässt. Steht direkt unter dem Knopf, nicht
    // erst in der LIESMICH.txt: eine Produkt-CSV sieht fertig aus.
    shopKitTodo:
      'Preis, Material und Versand füllst du selbst aus — sie stehen als Lücke in eckigen Klammern. Das Shopify-Produkt kommt als Entwurf herein, damit nichts halb ausgefüllt in den Verkauf geht.',
    shopKitProFeature: 'Das Shop-Paket ist eine Pro-Funktion.',
    // Die Lizenz der Vorlage, aus der dieses Projekt geöffnet wurde -- hier,
    // weil der Export die Stelle ist, an der aus einem Motiv eine Datei wird,
    // die jemand verkauft (Etsy-Runde).
    licenseTitle: 'Lizenz der Vorlage',
    licenseFrom: 'Dieses Projekt stammt aus „{title}“ — Lizenz: {license}.',
    licenseLabelPersonal: 'Privat',
    licenseLabelCommercial: 'Kommerziell',
    licenseLabelAttribution: 'Mit Namensnennung',
    licensePersonal: 'Verkaufen ist nicht erlaubt — weder die Datei noch das, was du damit schneidest.',
    licenseCommercial: 'Du darfst verkaufen, was du schneidest. Die Datei selbst weiterzugeben oder weiterzuverkaufen ist nicht gemeint.',
    licenseAttribution: 'Du darfst verkaufen, was du schneidest, solange du {author} nennst. Die Datei selbst weiterzugeben ist nicht gemeint.',
    licenseAuthorUnknown: 'die Urheberin',
    licenseProof: 'Lizenznachweis als PDF',
    licenseOpenTemplate: 'Zur Vorlage',
    // Kacheln (W6): Bahnen für Motive, die größer sind als die Matte.
    tilingProFeature: 'Kacheln ist eine Pro-Funktion.',
    exported: '{name} exportiert',
    failed: 'Export fehlgeschlagen.',
    saveFailed: 'Vor dem Export konnte nicht gespeichert werden.',
    // Steht hinter der Fehlermeldung, wenn „Überlappungen verschmelzen"
    // angehakt war: verschmolzen wurde für den Export, und ohne Export bleibt
    // davon nichts im Dokument stehen.
    weldReverted: 'Das Verschmelzen wurde zurückgenommen.',
    invalidNumber: 'Bitte eine Zahl eingeben, zum Beispiel 80,5.',
    // Dieselbe Spanne, die der Export-Controller prüft. Sie hier zu nennen
    // erspart einen Export, der erst nach dem Verschmelzen mit 422 abbricht.
    widthRange: 'Die Breite muss zwischen {min} und {max} mm liegen.',
    cutReport: 'Schnittbericht',
    // Die Matten-Ansicht: eine Matte je sichtbarer Ebene/Farbe, in der
    // Reihenfolge, in der die Geräte-Software die Farben aufruft. (`mat`
    // allein ist schon die Materialangabe weiter oben.)
    matsTitle: 'Matten — eine je Farbe',
    matNumber: 'Matte {number}',
    history: {
      title: 'Zuletzt exportiert',
      // Ehrlich benannt: es liegt keine Datei bereit, ein Klick baut sie neu.
      hint: 'Kein Datei-Cache — ein Klick erzeugt die Datei neu, mit denselben Angaben.',
      loading: 'Lädt…',
      loadFailed: 'Der Export-Verlauf konnte nicht geladen werden.',
      empty: 'Noch kein Export.',
      layersZip: 'ZIP je Ebene',
      printCut: 'Print & Cut',
      roleSplit: 'Gravur & Schnitt getrennt',
      bundle: 'Alles als ZIP',
      shopKit: 'Shop-Paket',
      // Steht als eigener Teil in der Zeile, wenn der Export gespiegelt war
      // -- ein Wiederholen baut dann wieder die gespiegelte Datei.
      mirrored: 'gespiegelt',
      tiling: 'Kacheln',
      restore: 'Erneut exportieren',
    },
    errors: {
      pro_required: 'Dieses Format braucht ein Pro-Konto.',
      empty_document: 'Dieses Projekt hat noch kein vektorisiertes Dokument.',
      writer_unavailable: 'Dieses Exportformat ist gerade nicht verfügbar. Bitte versuch es später noch einmal.',
      invalid_param: 'Ungültige Exportoptionen.',
      invalid_document: 'Dieses Dokument konnte nicht exportiert werden.',
      motif_too_large: 'Das Motiv passt nicht in den bedruckbaren Bereich dieser Seite. Wähle eine größere Seite oder eine kleinere Breite.',
      unsupported_raster: 'Das Projektbild ist kein PNG, JPEG oder WebP und kann nicht gedruckt werden.',
      export_timeout: 'Der Export hat zu lange gedauert und wurde abgebrochen. Versuch eine kleinere Breite oder ein einfacheres Dokument.',
      lint_failed: 'Die erzeugte Datei entspricht nicht den Regeln dieses Geräts und wurde nicht ausgeliefert. Bitte melde uns das Projekt.',
      nothing_to_split: 'Dieses Dokument hat nur Gravur- oder nur Schnittebenen — es gibt nichts zu trennen.',
    },
  },

  printCut: {
    title: 'Print & Cut',
    hint: 'Ein PDF zum Drucken mit Passermarken, und die Schnittlinie für den Plotter in denselben Koordinaten.',
    marks: 'Marken',
    page: 'Seite',
    pageWidth: 'Seitenbreite (mm)',
    pageHeight: 'Seitenhöhe (mm)',
    orientation: 'Ausrichtung',
    portrait: 'Hochformat',
    landscape: 'Querformat',
    bleed: 'Beschnitt {bleed}',
    includeDxf: 'Schnittlinie zusätzlich als DXF schreiben',
    preview: 'Vorschau: Bogen {width} × {height} mm mit {marks} Marken',
    proFeature: 'Print & Cut ist eine Pro-Funktion.',
    // Nur sichtbar, wenn „Für Bügelfolie spiegeln" angehakt ist: Print & Cut
    // nimmt den Spiegel bewusst nicht an -- der Bogen wird gedruckt und
    // geschnitten, nicht aufgebügelt, ein gespiegelter Druck wäre falsch.
    noMirror: 'Print & Cut wird nicht gespiegelt: der Bogen wird gedruckt und geschnitten, nicht aufgebügelt.',
    button: 'Print-&-Cut-ZIP',
    // M2: eigenes Busy-Label -- sonst heißt der Print-&-Cut-Knopf im
    // Ladezustand dasselbe wie die drei anderen ZIP-Knöpfe.
    exporting: 'Wird erstellt…',
  },

  // Kacheln (W6, Pro): ein Motiv, das größer ist als die Matte oder Bahn,
  // wird in überlappende Kacheln zerlegt — Schilder, Wandtattoos.
  tiling: {
    title: 'Kacheln',
    hint: 'Zerlegt das Motiv in überlappende Bahnen, eine Datei je Bahn. Die Schnittkanten an den Kachelgrenzen bleiben offen — die Bahnen werden überlappend verklebt, nicht geschnitten.',
    // Der Grund, warum die Knöpfe gerade gesperrt sind: es gibt nichts zu
    // zerlegen, der normale Export oben erledigt es.
    fits: 'Das Dokument passt auf die gewählte Kachelgröße — der normale Export reicht.',
    // Bewusst ohne das Wortpaar „Breite (mm)" — das Feld oben im Panel heißt
    // schon so, zwei gleichnamige Felder wären für Screenreader (und die
    // E2E-Locators) nicht unterscheidbar.
    tileWidth: 'Breite je Kachel (mm)',
    tileHeight: 'Höhe je Kachel (mm)',
    format: 'Dateityp je Bahn',
    overlap: 'Überlappung (mm)',
    marks: 'Übereinstimmungsmarken (Kreuze in der Überlappungszone)',
    // Der Name der Marken-Ebene in jeder Bahn-Datei (tiling.ts spricht kein
    // Deutsch, der Name kommt von hier).
    markLayerName: 'Klebemarken',
    // Wie viele Bahnen es werden, bevor jemand auf den Knopf drückt.
    grid: '{count} Kacheln — {rows} Reihen × {cols} Spalten, geklebt wird Zeile für Zeile.',
    invalid: 'Kachelseiten ab {min} mm, die Überlappung muss kleiner sein als die Kachel.',
    tooMany: 'Das wären mehr als {max} Kacheln. Wähle größere Kacheln oder weniger Überlappung.',
    preview: 'Vorschau: {count} Kacheln über dem Dokument',
    proFeature: 'Kacheln ist eine Pro-Funktion.',
    button: 'Kachel-ZIP',
    exporting: 'Wird gekachelt…',
  },

  text: {
    addTitle: 'Text hinzufügen',
    editTitle: 'Text bearbeiten',
    text: 'Text',
    placeholder: 'Frohe Ostern',
    font: 'Schrift',
    height: 'Höhe (mm)',
    alignment: 'Ausrichtung',
    left: 'Links',
    center: 'Zentriert',
    right: 'Rechts',
    groupSans: 'Serifenlos & Serif',
    groupScript: 'Schreibschrift',
    groupHandwriting: 'Handschrift',
    groupDisplay: 'Display',
    groupSingleLine: 'Schreiblinie (einlinig)',
    singleLineHint: 'Einlinig für den Stift-Plotter oder die Gravur: jeder Buchstabe ein Strich statt einer Fläche. Ohne Verschmelzen und ohne Verlauf entlang Pfad oder Kreis.',
    invalidHeight: 'Bitte eine Höhe zwischen {min} und {max} mm eingeben, zum Beispiel 20 oder 12,5.',
    editHint: 'Den Text neu zu setzen ersetzt jede Form in dieser Ebene — alles, was daran von Hand gemacht wurde (Knoten, Radierer, Verschmelzen), geht mit. Position und Größe bleiben.',
    addHint: 'Die Buchstaben werden zu Konturen in einer neuen Ebene — bereit zum Schneiden. Die Ebene merkt sich den Text, du kannst ihn später neu setzen. Die Höhe ist das Geviert; Großbuchstaben werden etwa 70 % davon.',
    nothingToSet: 'Nichts zu setzen — der Text hat keine Buchstaben.',
    fontFailed: 'Die Schrift konnte nicht geladen werden.',
    fontLoadFailed: 'Die Schrift konnte nicht geladen werden — bitte einmal online öffnen.',
    setting: 'Setzt…',
    update: 'Text aktualisieren',
    add: 'Text hinzufügen',
    cancel: 'Abbrechen',
    layout: 'Verlauf',
    layoutBlock: 'Als Block',
    layoutPath: 'Entlang Pfad',
    layoutCircle: 'Im Kreis',
    pathHint: 'Wähle vor dem Öffnen genau einen Pfad aus — er wird zum Leitpfad.',
    startOffset: 'Startversatz',
    startUnit: 'Einheit des Startversatzes',
    unitMm: 'mm',
    unitPercent: '%',
    spacing: 'Laufweite (mm)',
    side: 'Seite',
    sideTop: 'Oben',
    sideBottom: 'Unten',
    radius: 'Radius (mm)',
    invalidRadius: 'Bitte einen Radius zwischen {min} und {max} mm eingeben.',
    invalidNumber: 'Bitte eine Zahl eingeben, zum Beispiel 0 oder 2,5.',
    deleteGuide: 'Leitpfad löschen',
    weld: 'Buchstaben verschmelzen',
    weldTitle: 'Vereint die Buchstaben zu einer Silhouette — Löcher bleiben. Bei Schreibschriften ist das die Vorgabe.',
    pathHintCircle: 'Der Leitkreis ist nur ein Parameter — ins Dokument kommen nur die Buchstaben.',
    editPathHint: 'Beim Bearbeiten kehren die Buchstaben auf den gespeicherten Leitpfad zurück — auch wenn die Ebene inzwischen verschoben wurde.',
    skipped: { one: '1 Buchstabe passte nicht mehr auf den Leitpfad.', other: '{count} Buchstaben passten nicht mehr auf den Leitpfad.' },
    kerning: 'Feinabstand',
    kerningHint: 'Einen Buchstaben antippen und den Abstand davor anpassen — negativ rückt zusammen, er und alles danach rücken mit. Hoch/Tief hebt nur diesen Buchstaben (positiv = nach unten). Bleibt beim Neusetzen erhalten.',
    kerningDx: 'Abstand davor (mm)',
    kerningDy: 'Hoch/Tief (mm)',
    kerningReset: 'Zurücksetzen',
    kerningResetAll: 'Alle Feinabstände entfernen',
    kerningSpace: 'Leerzeichen',
    manualEditsWarning: 'Diese Ebene wurde seit dem letzten Setzen von Hand verändert (Knoten, Radierer, Verschmelzen o. Ä.). Den Text neu zu setzen verwirft diese Änderungen.',
  },

  mobile: {
    tabs: {
      select: 'Auswahl',
      edit: 'Bearbeiten',
      color: 'Farbe',
      paint: 'Malen',
      crop: 'Zuschneiden',
      layers: 'Ebenen',
      more: 'Mehr',
    },
    sheets: {
      layers: 'Ebenen',
      colors: 'Farbe',
      path: 'Pfad',
      paint: 'Malen',
      crop: 'Zuschneiden',
      more: 'Mehr',
      export: 'Export',
      arrange: 'Anordnen',
    },
    tool: 'Werkzeug',
    recolor: 'Umfärben',
    smartFill: 'Smarte Füllung',
    pick: 'Aufnehmen',
    brush: 'Pinsel',
    eraser: 'Radierer',
    // Die Überschrift über dem, was auf dem Desktop neben dem Namensfeld der
    // oberen Leiste steht — heute der KI-Namensvorschlag.
    projectSection: 'Projekt',
    textSection: 'Text',
    // Die Überschrift über der Liste, die auf dem Desktop das Einfügen-Menü
    // der Werkzeugleiste ist.
    insertSection: 'Einfügen',
    cutMeasureSection: 'Schneiden & Messen',
    flipSection: 'Spiegeln',
    flipHorizontal: 'Horizontal',
    flipVertical: 'Vertikal',
    addText: 'Text hinzufügen',
    community: 'Community',
    viewTemplate: 'Vorlage ansehen',
    renameTitle: 'Projekt umbenennen',
    renameLabel: 'Projektname',
    rename: 'Umbenennen',
    export: 'Export',
    actionBar: {
      nodeActions: 'Aktionen für Pfadknoten',
      selectionActions: 'Aktionen für die Auswahl',
      deleteNodes: 'Knoten löschen',
      delete: 'Löschen',
      doneEditing: 'Knoten fertig bearbeitet',
      done: 'Fertig',
      color: 'Farbe',
      additiveSelect: 'Mehrfachauswahl',
      additiveSelectOn: 'Mehrfachauswahl aus',
      duplicate: 'Duplizieren',
      forward: 'Eine Stufe nach vorn',
      back: 'Eine Stufe nach hinten',
      toFront: 'Ganz nach vorn',
      toBack: 'Ganz nach hinten',
      editNodes: 'Knoten bearbeiten',
      unite: 'Vereinen',
      subtract: 'Abziehen',
      contour: 'Kontur…',
      arrange: 'Anordnen',
      flipHorizontal: 'Horizontal spiegeln',
      flipVertical: 'Vertikal spiegeln',
      flipHorizontalHint: 'Für Bügelfolie spiegeln',
      penActions: 'Aktionen für den Stift',
      penUndoPoint: 'Letzten Punkt zurücknehmen',
      penDone: 'Pfad fertig zeichnen',
      group: 'Auswahl gruppieren',
      ungroup: 'Gruppierung aufheben',
    },
  },

  sheet: {
    resize: 'Bereich „{title}" in der Größe ändern',
    close: '{title} schließen',
  },

  toast: {
    dismiss: 'Hinweis schließen',
  },

  dialog: {
    close: 'Schließen',
    confirm: 'Bestätigen',
    cancel: 'Abbrechen',
    save: 'Speichern',
  },

  // Einfügen: das Menü an der Werkzeugleiste und der Dialog, der ein anderes
  // eigenes Projekt in das offene legt.
  insert: {
    menu: 'Einfügen',
    project: 'Projekt…',
    box: 'Schachtel…',
    qr: 'QR-Code…',
    testStrip: 'Teststreifen…',
    title: 'Projekt einfügen',
    search: 'Projekte durchsuchen…',
    noProjects: 'Kein anderes Projekt gefunden.',
    prevPage: 'Zurück',
    nextPage: 'Weiter',
    pageOf: 'Seite {page} von {pages}',
    position: 'Position',
    positionCenter: 'Mitte',
    positionPointer: 'An der letzten Zeigerposition',
    size: 'Größe',
    sizeOriginal: 'Original',
    sizeWidth: 'Auf Breite',
    widthMm: 'Breite (mm)',
    grow: 'Leinwand bei Bedarf vergrößern',
    submit: 'Einfügen',
    cancel: 'Abbrechen',
    loading: 'Lädt…',
    loadFailed: 'Das Projekt konnte nicht geladen werden.',
    nothing: 'Dieses Projekt hat keine Pfade.',
    done: { one: '1 Ebene eingefügt.', other: '{count} Ebenen eingefügt.' },
    undoLabel: 'Projekt „{name}" einfügen',
    mobileImport: 'Projekt einfügen…',
  },

  // Ein Projekt ohne Quellbild: leer angelegt oder aus einer Vorlage geöffnet.
  // Was daran anders ist, sagen diese vier Sätze -- an der Leinwand, am
  // gesperrten Werkzeug und im Raster-Panel.
  blank: {
    noImageTool: 'Kein Quellbild',
    noImagePanel: 'Dieses Projekt hat kein Quellbild — die smarte Füllung, Zuschneiden und der Foto-Modus brauchen eines.',
    emptyTitle: 'Leere Leinwand ({width} × {height} mm)',
    emptyBody: 'Füge Text hinzu (T) oder importiere über „Einfügen".',
  },

  // „Datei einfügen": Bilder und PDF-Scans laufen durch die Vektorisierung und
  // kosten einen Tagesslot, ein SVG liest der Browser selbst und kostet nichts.
  // Der Schachtel-Generator (Einfügen → Schachtel…): eine offene Schachtel
  // nach Maß, Schnitt- und Falz-Ebene getrennt (geometry/boxTemplate.ts).
  box: {
    title: 'Schachtel',
    length: 'Länge (mm)',
    width: 'Breite (mm)',
    height: 'Wandhöhe (mm)',
    flap: 'Klebelasche (mm)',
    create: 'Anlegen',
    cancel: 'Abbrechen',
    cutLayer: 'Schachtel — Schnitt',
    foldLayer: 'Schachtel — Falzen',
    invalid: 'Bitte Maße zwischen {min} und {max} mm eingeben.',
    done: 'Schachtel angelegt — Schnitt- und Falz-Ebene liegen oben.',
    hint: 'Boden mit vier Wänden und Klebelaschen. Die Falzlinien liegen auf einer eigenen Linien-Ebene; die Leinwand wächst mit, wenn die Abwicklung nicht darauf passt.',
  },

  // „Einfügen → QR-Code…": Text oder URL gehen an `POST /api/v1/qr_paths`
  // (rqrcode auf dem Server), das Modulraster kommt zurück und wird im
  // Browser zu Schnittpfaden (geometry/qrCode.ts) — kein SVG-Parsen, keine
  // Bild-Vektorisierung, keine Tagesquote.
  qr: {
    title: 'QR-Code',
    text: 'Text oder URL',
    textPlaceholder: 'https://… oder ein kurzer Text',
    size: 'Größe (mm)',
    create: 'Einfügen',
    cancel: 'Abbrechen',
    layer: 'QR',
    invalidText: 'Bitte einen Text zwischen 1 und 500 Zeichen eingeben.',
    invalidSize: 'Bitte eine Größe zwischen {min} und {max} mm eingeben.',
    requestFailed: 'Der QR-Code konnte nicht erzeugt werden.',
    done: 'QR-Code eingefügt — er liegt oben auf der Ebene „QR".',
    hint: 'Der helle Rand um den Code (die Ruhezone) gehört dazu — ohne ihn liest ihn keine Kamera zuverlässig. Die Ebene enthält nur die dunklen Module, keinen weißen Rücken: Material und Untergrund darunter müssen selbst hell bleiben, sonst verschwindet die Ruhezone im Kontrast und der Code liest sich nicht mehr.',
    errorCorrection: 'Fehlerkorrektur',
    levelL: 'L — 7 %, kleinster Code',
    levelM: 'M — 15 %',
    levelQ: 'Q — 25 %',
    levelH: 'H — 30 %, empfohlen',
    logo: 'Freibereich in der Mitte (% der Fläche, höchstens {max})',
    logoHint:
      'Der Freibereich bleibt leer — er ist ein Loch im Muster, kein Bild. Dein Logo legst du selbst hinein: als Ebene, die schon im Dokument liegt, oder über „Einfügen → Datei…".',
    logoArea: 'Das Loch wird etwa {size} mm im Quadrat — je nach Textlänge etwas mehr oder weniger.',
    logoTooLarge:
      'So viel Freibereich trägt dieser Code nicht: die Fehlerkorrektur müsste mehr ausgleichen, als sie kann. Höchstens {max} % gehen bei diesem Text.',
    logoImpossible:
      'Bei diesem Text ist kein Freibereich möglich: der Code braucht eine Version, bei der mitten im Muster ein Ausrichtungsmuster sitzt. Ein kürzerer Text (oder eine kurze Weiterleitungs-URL) lässt Platz fürs Logo.',
    useMaxLogo: 'Auf {max} % setzen',
    invalidPercent: 'Bitte einen Freibereich zwischen 0 und {max} % der Fläche eingeben.',
    style: 'Gestaltung',
    roundModules: 'Module abrunden',
    roundMarkers: 'Positionsmarken abrunden',
    levelRaised: 'Fehlerkorrektur auf {level} angehoben — ohne sie wäre der Freibereich nicht zu verantworten.',
    doneWithLogo: 'QR-Code eingefügt — der Freibereich in der Mitte ist {size} mm groß und wartet auf dein Logo.',
    tooSmall:
      'Ein Modul wird bei dieser Größe nur {mm} mm breit. Unter {min} mm liest das keine Kamera zuverlässig — größer anlegen oder weniger Text.',
    unreadable:
      'Der erzeugte Code liest sich nicht zurück: {count} Module stimmen nicht mit dem Muster überein. Nichts eingefügt — ein unlesbarer QR-Code sieht aus wie ein lesbarer.',
  },

  // „Einfügen → Teststreifen…": N gleich große Felder in einer Reihe, jedes
  // mit einer fortlaufenden Nummer darüber (geometry/testStrip.ts, Nummern
  // als Hershey-Simplex-Outline über singleLineFont.ts). Gedacht zum
  // Schneiden mit einem anderen Wert je Feld — welche Nummer zu welchem Wert
  // gehört, merkt sich die Nutzerin selbst; das Dokument speichert keinen
  // einzigen Schnittwert.
  testStrip: {
    title: 'Teststreifen',
    count: 'Anzahl Felder',
    fieldSize: 'Feldgröße (mm)',
    gap: 'Abstand (mm)',
    shape: 'Form',
    shapeSquare: 'Quadrat',
    shapeCircle: 'Kreis',
    create: 'Anlegen',
    cancel: 'Abbrechen',
    layerName: 'Teststreifen',
    invalidCount: 'Bitte eine Anzahl zwischen {min} und {max} Feldern eingeben.',
    invalidField: 'Bitte eine Feldgröße zwischen {min} und {max} mm eingeben.',
    invalidGap: 'Bitte einen Abstand zwischen {min} und {max} mm eingeben.',
    done: 'Teststreifen angelegt — Nummer und Schnittwert je Feld merkst du dir selbst.',
    hint: 'Jedes Feld trägt eine Nummer. Schneide die Reihe mit einem anderen Schnittwert je Feld und sieh, welches sauber löst — die Zuordnung Nummer→Wert steht nirgends im Dokument, nur bei dir am Gerät.',
  },

  insertFile: {
    menu: 'Datei…',
    mobile: 'Datei einfügen…',
    title: 'Datei einfügen',
    drop: 'PNG, JPEG, WebP, HEIC, AVIF, PDF oder SVG hierher ziehen — oder',
    choose: 'Datei auswählen',
    // Zwei Mengen in einem Satz, jede für sich gebeugt: „1 Pfad in 1 Ebene".
    svgSummary: '{paths} in {layers}',
    svgPaths: { one: '1 Pfad', other: '{count} Pfade' },
    svgLayers: { one: '1 Ebene', other: '{count} Ebenen' },
    quotaHint: 'Kostet eine Vektorisierung deiner Tagesquote.',
    quotaHintPro: 'Kostet eine Vektorisierung.',
    // PDF-Seitenwahl (Task 6, round four): erscheint nur, wenn die gewählte
    // Datei eine PDF ist.
    pdfPage: 'PDF-Seite',
    position: 'Position',
    positionCenter: 'In der Mitte',
    positionPointer: 'An der letzten Zeigerposition',
    size: 'Größe',
    sizeOriginal: 'Originalgröße',
    sizeWidth: 'Auf Breite',
    widthMm: 'Breite (mm)',
    invalidWidth: 'Bitte eine Breite in Millimetern eingeben, zum Beispiel 80,5.',
    grow: 'Leinwand mitwachsen lassen',
    submit: 'Einfügen',
    cancel: 'Abbrechen',
    uploading: 'Lädt hoch…',
    converting: 'Vektorisiert ({status})…',
    // Die Stufen einer Conversion, wie das Dashboard sie auch nennt
    // (components/ProjectsDashboard.tsx).
    statuses: {
      pending: 'wartet',
      preprocessing: 'Vorbereitung',
      tracing: 'läuft',
      postprocessing: 'Nachbearbeitung',
    },
    done: { one: '1 Ebene eingefügt.', other: '{count} Ebenen eingefügt.' },
    // Nach einem fehlgeschlagenen Bild: der Slot ist weg, und das soll die
    // Nutzerin wissen, bevor sie es noch einmal versucht.
    retryCosts: 'Ein neuer Versuch kostet eine weitere Vektorisierung deiner Tagesquote.',
    undoSvg: 'SVG „{name}" einfügen',
    undoImage: 'Bild „{name}" einfügen',
    // „Motiv erstellen" im selben Dialog: der Vorschlag kommt vom externen
    // Bilddienst (docs/GENERATION.md) und geht danach durch denselben
    // Bild-Zweig wie eine gewählte Datei.
    generateLegend: 'Oder ein Motiv beschreiben',
    generatePlaceholder: 'z. B. Ein Anker mit Tau',
    generateStyle: 'Stil',
    generateRun: 'Vorschlag erstellen',
    generateBusy: 'Vorschlag wird erstellt…',
    generateHint: 'Die Beschreibung geht an einen externen Bilddienst; jeder Vorschlag kostet einen Slot des KI-Tageslimits.',
    generateLimit: 'Das Monatskontingent für KI-Bilder ist erreicht. Unter Konto kannst du 20 weitere Requests für 4,99 € kaufen.',
    generateUnavailable: 'Der Bilddienst ist gerade nicht verfügbar. Versuch es später noch einmal.',
    generateFailed: 'Der Vorschlag konnte nicht erstellt werden. Bitte versuch es noch einmal.',
    errors: {
      'too-many-nodes': 'Das SVG hat mehr als 20.000 Knoten.',
      empty: 'Das SVG enthält keine Pfade.',
      invalid: 'Die Datei ist kein gültiges SVG.',
      unsupportedType: 'Nur PNG, JPEG, WebP, HEIC, AVIF, PDF, AI, EPS oder SVG.',
      tooLarge: 'Die Datei ist größer als 20 MB.',
      limitReached: 'Tageslimit erreicht ({used} / {limit}).',
      conversionFailed: 'Die Vektorisierung ist fehlgeschlagen.',
      emptyResult: 'Die Vektorisierung hat keine Ebenen ergeben. Ein anderes Bild oder andere Einstellungen helfen meist.',
      uploadFailed: 'Die Datei konnte nicht hochgeladen werden.',
      // PDF-Seitenwahl (Task 6): kein Slot verbraucht, also kein `retry`-Hinweis
      // wie bei einer fehlgeschlagenen Vektorisierung.
      pdfPageOutOfRange: {
        one: 'Diese Seite gibt es nicht — die PDF hat nur 1 Seite.',
        other: 'Diese Seite gibt es nicht — die PDF hat {count} Seiten.',
      },
      // Runde 4, Task 5 (I5): dieselben Sätze wie die eingefrorenen
      // `UserFacingError`-Konstanten der Engines (skeleton.rb, postprocess.rb) —
      // wortgleich, damit sich für die deutsche Oberfläche nichts ändert, nur
      // die englische bekommt jetzt eine Übersetzung statt des rohen Servertexts
      // (Vectorization::UserFacingError::CODES ordnet den Text einem dieser
      // Codes zu; ein unbekannter Text bleibt beim Servertext).
      too_fine:
        'Die Linien sind zu fein für die Mittellinien-Erkennung — beim Vorbereiten des Bildes bleibt nichts von ihnen übrig. Am besten eine Zeichnung mit kräftigeren Strichen verwenden oder sie als Fläche vektorisieren (vtracer).',
      too_many_points:
        'Aus diesem Bild werden zu viele einzelne Linien — als Mittellinie ließe es sich nicht mehr bearbeiten. Am besten eine klare Strichzeichnung verwenden; für Farbflächen ist vtracer die richtige Engine.',
      unfit_document:
        'Aus diesem Bild wird keine Schnittdatei, die sich noch bearbeiten lässt — die Konturen sind zu fein. Versuch es mit weniger Farben oder einer höheren Kleinteil-Schwelle.',
      // Task 7 (Altlasten-Runde): derselbe Satz wie `Normalize::PDF_ERROR`.
      pdf_unreadable:
        'Die PDF-Datei konnte nicht gelesen werden. Verschlüsselte und leere PDFs lassen sich nicht vektorisieren.',
    },
  },

  // Der SVG-Doktor (docs/superpowers/specs/2026-08-30-svg-doctor-design.md).
  //
  // Ein Befund sagt zwei Dinge: *was* in der Datei steht (die Zeile, gebeugt
  // über die Zahl) und *was daraus wird* (die Schwere als Wort davor). Die
  // Schwere ist absichtlich Text und keine Farbe -- die Liste steht in zwei
  // Dialogen, wird vorgelesen und muss auch ohne Farbe zu lesen sein.
  svgDoctor: {
    severity: {
      blocked: 'Geht verloren',
      dropped: 'Wird entfernt',
      repairable: 'Reparierbar',
      note: 'Hinweis',
    },
    findings: {
      'embedded-image': {
        one: '1 eingebettetes Bild — ein Plotter schneidet daraus nichts.',
        other: '{count} eingebettete Bilder — ein Plotter schneidet daraus nichts.',
      },
      'external-image': {
        one: '1 Bild von einem fremden Server — es lässt sich hier nicht holen.',
        other: '{count} Bilder von fremden Servern — sie lassen sich hier nicht holen.',
      },
      text: {
        one: '1 Textelement — Schnittklar hat die Schriftart der Datei nicht. Setze den Text im Editor neu.',
        other: '{count} Textelemente — Schnittklar hat die Schriftart der Datei nicht. Setze den Text im Editor neu.',
      },
      'clip-path': {
        one: '1 Beschneidungspfad — was er verdeckt, käme sonst still wieder zum Vorschein.',
        other: '{count} Beschneidungspfade — was sie verdecken, käme sonst still wieder zum Vorschein.',
      },
      mask: { one: '1 Maske — eine Klinge kennt keine Maske.', other: '{count} Masken — eine Klinge kennt keine Maske.' },
      filter: {
        one: '1 Filter — Weichzeichner und Schatten haben keine Schnittkante.',
        other: '{count} Filter — Weichzeichner und Schatten haben keine Schnittkante.',
      },
      'foreign-object': {
        one: '1 eingebettetes HTML-Element.',
        other: '{count} eingebettete HTML-Elemente.',
      },
      gradient: {
        one: '1 Farbverlauf — er wird zu einer Ebene in Schwarz.',
        other: '{count} Farbverläufe — sie werden zu einer Ebene in Schwarz.',
      },
      'style-block': {
        one: '1 <style>-Block — Farben aus CSS-Klassen kommen nicht mit.',
        other: '{count} <style>-Blöcke — Farben aus CSS-Klassen kommen nicht mit.',
      },
      'use-element': {
        one: '1 Verweis auf eine andere Form (<use>) — er wird aufgelöst.',
        other: '{count} Verweise auf andere Formen (<use>) — sie werden aufgelöst.',
      },
      'no-units': {
        one: 'Die Datei sagt nicht, wie groß sie ist — gerechnet wird mit 96 dpi.',
        other: 'Die Datei sagt nicht, wie groß sie ist — gerechnet wird mit 96 dpi.',
      },
      duplicates: {
        one: '1 doppelter Pfad — er würde zweimal geschnitten.',
        other: '{count} doppelte Pfade — sie würden zweimal geschnitten.',
      },
      'small-parts': {
        one: '1 Teil unter 1 mm — es bleibt auf der Matte liegen.',
        other: '{count} Teile unter 1 mm — sie bleiben auf der Matte liegen.',
      },
      overlaps: {
        one: '1 Ebene mit überlappenden Pfaden — die Kante wird doppelt gefahren.',
        other: '{count} Ebenen mit überlappenden Pfaden — die Kanten werden doppelt gefahren.',
      },
      'node-budget': {
        one: '1 Knoten über dem, was ein Plotter flüssig fährt.',
        other: '{count} Knoten über dem, was ein Plotter flüssig fährt.',
      },
      'stroke-only': {
        one: '1 Ebene besteht nur aus Konturen — geschnitten wird die Linie, nicht die Fläche.',
        other: '{count} Ebenen bestehen nur aus Konturen — geschnitten wird die Linie, nicht die Fläche.',
      },
      'loose-parts': {
        one: '1 loses Innenteil — es fiele beim Schneiden einer Schablone heraus.',
        other: '{count} lose Innenteile — sie fielen beim Schneiden einer Schablone heraus.',
      },
      // Die zwei Befunde des echten Vektorimports (PDF/AI/EPS).
      'outlined-text': {
        one: '1 Zeichen kam als Umriss an — schneidbar, aber nicht mehr als Schrift änderbar.',
        other: '{count} Zeichen kamen als Umrisse an — schneidbar, aber nicht mehr als Schrift änderbar.',
      },
      transparency: {
        one: '1 teildeckende Fläche — sie wird voll geschnitten, ein Plotter kennt kein „halb da".',
        other: '{count} teildeckende Flächen — sie werden voll geschnitten, ein Plotter kennt kein „halb da".',
      },
    },
    // Die Schalter des Befund-Dialogs. Ihre Zahl kommt aus der Reparatur und
    // nicht aus dem Befund: sie sagt, was dieser Schalter wirklich ändert.
    repairs: {
      duplicates: {
        one: '1 doppelten Pfad entfernen',
        other: '{count} doppelte Pfade entfernen',
      },
      'small-parts': {
        one: '1 Teil unter 1 mm entfernen',
        other: '{count} Teile unter 1 mm entfernen',
      },
      overlaps: {
        one: 'Überlappungen in 1 Ebene verschmelzen',
        other: 'Überlappungen in {count} Ebenen verschmelzen',
      },
      'node-budget': {
        one: '1 Knoten einsparen (verändert die Form leicht)',
        other: '{count} Knoten einsparen (verändert die Form leicht)',
      },
    },
    dialog: {
      title: 'SVG reparieren',
      reading: 'Die Datei wird gelesen…',
      // Zwei Sätze statt einem: `tn` beugt eine Zahl, und der Dialog hat
      // zwei. `svgDoctor.summaryMessage` setzt sie zusammen.
      summaryPaths: {
        one: '1 schneidbarer Pfad',
        other: '{count} schneidbare Pfade',
      },
      summaryLayers: {
        one: 'in 1 Ebene.',
        other: 'in {count} Ebenen.',
      },
      repairs: 'Beim Anlegen reparieren',
      width: 'Zielbreite (mm)',
      widthHint: '{min} bis {max} mm — die Höhe folgt dem Seitenverhältnis.',
      rescueHint:
        'In dieser Datei steckt kein Schnitt, sondern ein Bild. Schnittklar kann es vektorisieren — das kostet einen deiner Tagesslots.',
      rescue: 'Bild herausziehen und vektorisieren',
      create: 'Projekt anlegen',
      creating: 'Wird angelegt…',
      cancel: 'Abbrechen',
    },
    errors: {
      empty: 'In dieser Datei ist nichts, was ein Plotter schneiden könnte.',
      'too-many-nodes': 'Diese Datei hat zu viele Knoten für den Editor. Vereinfache sie in deinem Zeichenprogramm.',
      invalid: 'Diese Datei ist keine lesbare SVG-Datei.',
      width: 'Die Zielbreite muss zwischen {min} und {max} mm liegen.',
      create: 'Das Projekt konnte nicht angelegt werden. Bitte versuch es noch einmal.',
      rateLimited: 'Zu viele SVG-Dateien in kurzer Zeit — versuch es später noch einmal.',
      read: 'Die Datei konnte nicht gelesen werden. Wähle sie noch einmal aus.',
      // Die Höhe folgt dem Seitenverhältnis: ein sehr hohes Motiv sprengt bei
      // voller Breite die Seite, die der Server annimmt. Ein zweiter Versuch
      // ändert daran nichts — eine kleinere Zielbreite schon.
      tooTall: 'Bei {width} mm Breite wäre das Motiv {height} mm hoch — mehr als {max} mm. Wähle eine kleinere Zielbreite.',
      invalidDocument: 'Aus dieser Datei lässt sich kein Projekt bauen, das Schnittklar annimmt.',
      // Die ehrliche Absage an den Grenzen des Dokumentschemas: mit Zahl und
      // Grenze, damit daraus hervorgeht, was zu tun ist. Ein zweiter Versuch
      // ändert daran nichts.
      'too-many-layers':
        'Diese Datei hat {count} Farben und damit {count} Ebenen — Schnittklar hält {limit}. ' +
        'Lege Farben in deinem Zeichenprogramm zusammen.',
      'too-many-paths':
        'Eine Ebene dieser Datei hat {count} Pfade — Schnittklar hält {limit} je Ebene. ' +
        'Vereinfache die Datei in deinem Zeichenprogramm.',
      'path-too-long':
        'Ein einzelner Pfad dieser Datei ist zu verwickelt für den Editor ' +
        '({count} Zeichen, die Grenze liegt bei {limit}). Vereinfache ihn in deinem Zeichenprogramm.',
    },
  },

  // Der echte Vektorimport: was passiert, wenn jemand eine PDF-, AI- oder
  // EPS-Datei auswählt, in der Pfade stecken (`lib/vectorSource.ts`).
  vectorSource: {
    title: 'Diese Datei enthält Vektoren',
    // Der Satz, um den es in der Anforderung geht. Er nennt beide Wege und
    // sagt, was sie unterscheidet -- „verlustfrei" ist der Grund, warum
    // jemand diese Datei gekauft hat.
    body:
      'In dieser Datei stecken fertige Pfade. Schnittklar kann sie direkt übernehmen — ' +
      'verlustfrei, in den Originalfarben und ohne einen Slot deiner Tagesmenge. ' +
      'Oder die Seite wird wie bisher gerastert und neu vektorisiert.',
    pageHint: 'Seite {page} von {pages}.',
    direct: 'Vektoren übernehmen',
    retrace: 'Neu vektorisieren',
    cancel: 'Abbrechen',
    // Das Werkzeug fehlt auf diesem Server. Kein Fehler der Datei -- und
    // deshalb keine Absage, sondern der bisherige Weg ohne Umstände.
    unavailable: 'Diese Datei enthält Vektoren, aber dieser Server kann sie nicht direkt übernehmen.',
    failed: 'Die Datei konnte nicht auf Vektoren geprüft werden. Sie wird wie bisher vektorisiert.',
  },

  ai: {
    title: 'KI',
    // Derselbe Wortlaut wie auf der Preisseite (app/models/billing.rb), damit
    // das Panel verspricht, was dort verkauft wird, und nichts anderes.
    proFeature: 'Hintergrund automatisch entfernen (läuft im Browser)',
    proHint: '{feature} — eine Pro-Funktion.',
    phaseRuntime: 'KI-Laufzeit wird geladen',
    phaseDownload: 'Modell wird geladen',
    phaseLoad: 'Modell wird vorbereitet',
    phaseInfer: 'Bild wird berechnet',
    // Der Fortschritt eines Downloads: mit Prozentzahl, wenn der Server seine
    // Größe genannt hat, sonst nur die geladenen Megabyte. Die Laufzeit ist
    // 28 MB — ohne Zahl daneben sieht eine langsame Leitung aus wie ein Hänger.
    phaseBytes: '{phase} — {done} von {total} ({percent} %)',
    phaseBytesUnknown: '{phase} — {done} geladen',
    errorUnsupported: 'Dieser Browser kann die Modelle nicht ausführen (kein WebAssembly mit SIMD).',
    errorDownload: 'Das Modell konnte nicht geladen werden. Bitte Verbindung prüfen.',
    errorInference: 'Die Berechnung ist fehlgeschlagen.',
    errorCancelled: 'Abgebrochen.',
    errorStalled: 'Seit 60 Sekunden kein Fortschritt mehr — abgebrochen.',
    removeBackground: 'Hintergrund entfernen',
    upscale: 'Hochskalieren ×2',
    checkingBrowser: 'Prüfe Browser…',
    backendWebgpu: 'Läuft im Browser über WebGPU.',
    backendWasm: 'Läuft im Browser über WebAssembly.',
    noImage: 'Dieses Projekt hat kein Bild.',
    replaceFailed: 'Das Arbeitsbild konnte nicht ersetzt werden.',
    uploadFailed: 'Das neue Arbeitsbild konnte nicht hochgeladen werden. Es bleibt im Editor und wird erneut versucht.',
    replaced: 'Arbeitsbild ersetzt.',
    retraceTitle: 'Neu vektorisieren',
    retraceConfirm: 'Neu vektorisieren',
    replacedRetracing: 'Arbeitsbild ersetzt, Bild wird neu vektorisiert…',
    // Wenn das neue Arbeitsbild den Server nicht erreicht hat, darf nicht neu
    // vektorisiert werden — der Server hätte das alte Bild gezeichnet. Der
    // Satz sagt beides: was jetzt gilt und wie es nachzuholen ist.
    retraceSkipped:
      'Das Arbeitsbild ist ersetzt, aber nicht beim Server angekommen — deshalb wurde nicht neu vektorisiert. Die Vektoren gehören noch zum alten Bild. Sobald oben „Gespeichert" steht, startest du sie im Panel „Neu vektorisieren".',
    limitReached: 'Tageslimit erreicht — das Arbeitsbild ist ersetzt, die Vektorisierung nicht gestartet.',
    retraceFailed: 'Die Vektorisierung konnte nicht gestartet werden. Das Arbeitsbild ist ersetzt.',
    tooLargeTitle: 'Das Ergebnis wäre größer als {max} px.',
    tooLargeHint: 'Hochskalieren ist aus: das Ergebnis wäre größer als {max} px.',
    starting: 'Starte…',
    phaseRunning: '{phase} …',
    cancel: 'Abbrechen',
    retry: 'Erneut versuchen',
    before: 'Vorher',
    after: 'Nachher',
    applyReplace: 'Anwenden — Arbeitsbild ersetzen',
    applyRetrace: '… und neu vektorisieren',
    discard: 'Verwerfen',
    // Die Bearbeitung nach Anweisung: läuft anders als die zwei Funktionen
    // darüber nicht im Browser, sondern beim Bilddienst (docs/GENERATION.md).
    edit: 'KI-Bearbeitung',
    editLabel: 'Mit Anweisung bearbeiten',
    editPlaceholder: 'z. B. Entferne die Schrift unten links',
    editRun: 'Bearbeiten',
    editWorking: 'Bild wird bearbeitet — das kann bis zu einer Minute dauern…',
    editHint: 'Dafür wird das Bild an einen externen Bilddienst übertragen.',
    editFailed: 'Die Bearbeitung ist fehlgeschlagen. Versuch es noch einmal.',
    editUnavailable: 'Der Bilddienst ist gerade nicht verfügbar. Versuch es später noch einmal.',
    editLimit: 'Das Monatskontingent für KI-Bilder ist erreicht. Unter Konto kannst du 20 weitere Requests für 4,99 € kaufen.',
    // Ein-Klick-Vorschläge über dem Textfeld: sie füllen den Prompt (ersetzen
    // ihn, hängen nicht an) und fokussieren das Feld, senden aber nichts —
    // das Abschicken bleibt eine bewusste Handlung, weil es die Quote kostet.
    presetComplete: 'Motiv vervollständigen',
    presetRepair: 'Beschädigtes reparieren',
    presetCompletePrompt:
      'Vervollständige das angeschnittene Motiv nahtlos im selben Stil, flächige Farben, klare Konturen, weißer Hintergrund.',
    presetRepairPrompt:
      'Repariere die beschädigten Stellen nahtlos im selben Stil, flächige Farben, klare Konturen, weißer Hintergrund.',
  },

  publish: {
    title: 'Veröffentlichen',
    titleNew: 'Als Vorlage veröffentlichen',
    titleUpdate: 'Vorlage aktualisieren',
    serverFallback: 'Das hat nicht geklappt. Versuch es gleich noch einmal.',
    needsAlias: 'Vorlagen tragen immer einen Autor-Handle — sonst führt der Link auf der Vorlagenseite nirgendwohin. Lege zuerst einen Alias in deinem Konto an.',
    needsPublicProfile: 'Die Vorlage verlinkt auf dein Profil, und das ist gerade privat — der Link ginge für alle anderen ins Leere. Stell zuerst dein Profil öffentlich.',
    cancel: 'Abbrechen',
    toAccount: 'Zum Konto',
    updated: 'Vorlage aktualisiert.',
    published: 'Vorlage veröffentlicht.',
    view: 'Ansehen',
    fieldTitle: 'Titel',
    fieldDescription: 'Beschreibung (optional)',
    // Zweisprachig veröffentlichen (2026-10-19): Deutsch bleibt die Basis für
    // Katalog, Filter und Tags, Englisch kommt dazu. Der Umschalter wählt nur,
    // welche Felder zu sehen sind -- beide Fassungen gehen mit.
    languageLegend: 'Sprache der Texte',
    languageGerman: 'Deutsch',
    languageEnglish: 'English',
    englishHint: 'Optional: So erscheint die Vorlage für Besucher, die Schnittklar auf Englisch nutzen. Die englischen Tags stehen in derselben Reihenfolge wie die deutschen.',
    germanRequiredHint: 'Der deutsche Titel ist Pflicht – der Katalog sucht und filtert auf Deutsch.',
    translateFromGerman: 'Aus dem Deutschen übersetzen',
    translateFromEnglish: 'Aus dem Englischen übersetzen',
    translating: 'Übersetze …',
    translated: 'Übersetzt – bitte lesen und bei Bedarf anpassen.',
    translateProHint: 'Mit Pro lässt sich die andere Sprache per Klick übersetzen.',
    translateOverwrite: 'Die Felder dieser Sprache sind schon ausgefüllt. Durch die Übersetzung ersetzen?',
    translatePro: 'Übersetzen ist eine Pro-Funktion.',
    translateLimit: 'Zu viele Übersetzungen in dieser Stunde. Versuch es später noch einmal.',
    translateUnavailable: 'Übersetzen ist gerade nicht verfügbar. Versuch es später noch einmal.',
    translateFailed: 'Das Übersetzen hat nicht geklappt.',
    fieldTags: 'Tags, mit Komma getrennt',
    tagsPlaceholder: 'ostern, kinder, flex',
    licenseLegend: 'Was dürfen andere damit machen?',
    fieldProfile: 'Geräteprofil',
    includeSourceImage: 'Originalbild mitveröffentlichen',
    rightsPrefix: 'Ich habe die Rechte an diesem Motiv und akzeptiere die',
    rightsLink: 'Vorlagen-Bedingungen',
    sending: 'Einen Moment…',
    submit: 'Veröffentlichen',
    licensePrivate: 'Privat',
    licensePrivateHint: 'Andere dürfen schneiden und verschenken, aber nichts damit verkaufen.',
    licenseCommercial: 'Kommerziell',
    licenseCommercialHint: 'Andere dürfen verkaufen, was sie damit schneiden — die Datei selbst nicht.',
    licenseAttribution: 'Mit Namensnennung',
    licenseAttributionHint: 'Wie kommerziell, aber dein Name muss dabeistehen.',
    profileAny: 'Jedes Gerät',
    profileCricut: 'Cricut',
    profileXtool: 'xTool',
    // Schwierigkeit, Zeitaufwand und Materialliste (4.5/4.6, Runde 5) --
    // alle drei optional, deshalb die Platzhalter „keine Angabe“.
    fieldDifficulty: 'Schwierigkeit (optional)',
    difficultyPlaceholder: 'Keine Angabe',
    difficultyEasy: 'Leicht',
    difficultyMedium: 'Mittel',
    difficultyChallenging: 'Anspruchsvoll',
    fieldTimeMinutes: 'Zeitaufwand in Minuten (optional)',
    fieldMaterials: 'Materialliste, mit Komma getrennt (optional)',
    // Vorbefüllt aus den Ebenenfarben (siehe `materialSuggestions`) -- der
    // Platzhalter zeigt trotzdem ein Beispiel, für den Fall, dass die
    // Autorin die Vorschläge löscht.
    materialsPlaceholder: 'Folie Blau, Übertragungsband',
    validation: {
      title: 'Gib deiner Vorlage einen Titel.',
      germanTitle: 'Der deutsche Titel fehlt – trag ihn unter „Deutsch" ein oder lass ihn übersetzen.',
      maxLength: 'Höchstens {max} Zeichen.',
      tooManyTags: 'Höchstens {max} Tags — es sind {count}.',
      tagLength: 'Jeder Tag darf höchstens {max} Zeichen lang sein.',
      timeMinutes: 'Zwischen {min} und {max} Minuten.',
      tooManyMaterials: 'Höchstens {max} Materialien — es sind {count}.',
      materialLength: 'Jedes Material darf höchstens {max} Zeichen lang sein.',
      rights: 'Ohne diese Bestätigung geht es nicht.',
    },
    // Die Herkunft einer Ableitung (docs/COMMUNITY.md, „Remix"): der Hinweis
    // über dem ersten Feld und die Sackgasse, wenn die Elternvorlage keine
    // Ableitungen erlaubt.
    remixNotice: 'Wird als Ableitung von „{title}“ von {author} veröffentlicht — die Herkunft steht auf der Vorlagenseite.',
    // Wenn die Autorin der Elternvorlage keinen Alias hat: aus `null` darf
    // kein „@null" werden.
    remixAuthorFallback: 'der Autorin',
    remixBlocked: 'Diese Vorlage ist nur für den persönlichen Gebrauch freigegeben — eine Ableitung davon kannst du nicht veröffentlichen.',
    // Der Duplikat-Hinweis (Task 10): niemals eine Sperre, nur ein Blick in
    // den Katalog, bevor abgeschickt wird — die Titel danach sind Links.
    duplicateHint: 'Sieht ähnlich aus wie:',
    // Was der Treffer bedeutet -- der Satz, der aus der Auskunft eine
    // Entscheidung macht. Zuerst die Entwarnung (kein Vorwurf, keine
    // Sperre), dann der Weg, den es schon gibt: als Remix veröffentlichen
    // nennt die Quelle, ein zweites eigenes Motiv daneben nicht.
    duplicateAdvice:
      'Das ist kein Vorwurf und keine Sperre — zwei Menschen dürfen denselben Osterhasen zeichnen. ' +
      'Hast du eines dieser Motive als Vorlage geöffnet und weiterbearbeitet, dann brich hier ab, ' +
      'öffne es über „In meinen Projekten öffnen" und veröffentliche das Ergebnis von dort: als Remix ' +
      'nennt es seine Quelle und zählt der Urheberin mit.',
    // Dieselbe Entwarnung, aber ohne den Remix-Rat: diese Motive sind nur für
    // den persönlichen Gebrauch freigegeben, eine Ableitung lehnt der Server
    // ab (`remix_not_allowed`).
    duplicateAdviceNoRemix:
      'Das ist kein Vorwurf und keine Sperre — zwei Menschen dürfen denselben Osterhasen zeichnen. ' +
      'Eine Ableitung ist hier allerdings nicht der Weg: diese Motive sind nur für den persönlichen ' +
      'Gebrauch freigegeben. Veröffentliche also nur, was aus deiner eigenen Zeichnung entstanden ist.',
    errors: {
      rights_not_confirmed: 'Ohne die Bestätigung der Rechte geht es nicht.',
      alias_required: 'Lege zuerst einen Alias in deinem Konto an.',
      profile_not_public: 'Stell zuerst dein Profil öffentlich — die Vorlage verlinkt darauf.',
      email_unconfirmed: 'Bestätige zuerst deine E-Mail-Adresse — der Link dazu liegt in deinem Postfach.',
      empty_document: 'Dieses Projekt hat noch kein Dokument, das sich veröffentlichen ließe.',
      invalid: 'Das Formular ist noch nicht vollständig.',
      not_found: 'Dieses Projekt gibt es nicht mehr.',
      parent_hidden: 'Die Vorlage, aus der dieses Projekt stammt, ist gerade nicht im Katalog — warte, bis sie zurück ist.',
      paid_source: 'Dieses Projekt stammt aus einem gekauften Vorlagenpaket. Die Motive daraus darfst du schneiden und verkaufen, aber nicht selbst im Katalog veröffentlichen.',
    },
  },

  // Der Teilen-Dialog (S-8, docs/superpowers/specs/2026-08-29-share-link-design.md).
  // Ein Link ist keine Veröffentlichung: kein Katalog, keine Lizenz, keine
  // Moderation -- deshalb steht hier „teilen" und nirgends „veröffentlichen".
  share: {
    title: 'Ergebnis teilen',
    open: 'Teilen',
    // Auf dem Knopf, solange es nichts zu teilen gibt (S-10).
    emptyHint: 'Erst vektorisieren, dann teilen: dieses Projekt hat noch keine Ebenen.',
    active: 'Link aktiv',
    // Was der Empfänger sieht -- und was er nicht sieht. Der Satz steht direkt
    // am Schalter, weil er die Entscheidung ist, die dort getroffen wird.
    intro: 'Wer den Link hat, sieht den Projektnamen, das Ergebnis und die Ebenen und kann das SVG herunterladen. Dein Originalbild bleibt hier — es geht nie mit.',
    off: 'Der Link ist aus. Schalte ihn an, dann bekommst du eine Adresse zum Verschicken.',
    urlLabel: 'Link zum Verschicken',
    copy: 'Kopieren',
    copied: 'Kopiert',
    copyFailed: 'Kopieren hat hier nicht geklappt — der Link ist markiert, kopier ihn mit Strg/⌘+C.',
    views: { one: '1 Aufruf', other: '{count} Aufrufe' },
    rotate: 'Neuen Link erzeugen',
    rotateTitle: 'Neuen Link erzeugen?',
    rotateBody: 'Der alte Link wird sofort ungültig — wer ihn schon hat, sieht danach nur noch eine Fehlerseite. Den neuen musst du selbst noch einmal verschicken.',
    rotateConfirm: 'Neuen Link erzeugen',
    done: 'Schließen',
    serverFallback: 'Das hat nicht geklappt. Versuch es gleich noch einmal.',
    errors: {
      empty_document: 'Dieses Projekt hat noch kein Ergebnis, das sich teilen ließe.',
      not_found: 'Dieses Projekt gibt es nicht mehr.',
      forbidden: 'Teilen kann nur, wem das Projekt gehört.',
      too_many_requests: 'Zu viele Änderungen in kurzer Zeit — versuch es gleich noch einmal.',
    },
  },

  // Der Versionsverlauf (docs/EDITOR.md, „Versionen"): das Panel, die
  // Vorschau und die Rückfragen. Nur das Vektordokument -- das Arbeitsbild
  // hat keinen Verlauf, und das Panel sagt das lieber, als es zu verschweigen.
  history: {
    title: 'Verlauf',
    dialogTitle: 'Versionen',
    intro: 'Frühere Stände des Dokuments. Das Arbeitsbild wird nicht versioniert.',
    loading: 'Verlauf wird geladen…',
    loadFailed: 'Der Verlauf ließ sich nicht laden.',
    empty: 'Noch keine Versionen — die erste entsteht beim nächsten Speichern.',
    save: 'Version sichern…',
    saveTitle: 'Version sichern',
    saveLabel: 'Bezeichnung (optional)',
    saveConfirm: 'Sichern',
    saved: 'Version gesichert.',
    saveFailed: 'Die Version ließ sich nicht sichern.',
    saveEmpty: 'Ein leeres Blatt hat keinen Stand, den man sichern könnte.',
    preview: 'Vorschau von {title}',
    compareCurrent: 'Aktuell',
    compareVersion: 'Diese Version',
    restore: 'Wiederherstellen',
    restoreTitle: 'Diesen Stand wiederherstellen?',
    restoreBody: 'Das Dokument wird auf „{title}“ ({time}) gesetzt. Der jetzige Stand wird vorher als Version gesichert; Rückgängig beginnt danach von vorn.',
    restoreUnsavedBody: 'Ungespeicherte Änderungen gehen verloren: sie ließen sich gerade nicht speichern. Das Dokument wird auf „{title}“ ({time}) gesetzt; der letzte gespeicherte Stand wird vorher als Version gesichert.',
    restoreConfirm: 'Wiederherstellen',
    restored: 'Stand wiederhergestellt — der vorherige liegt im Verlauf.',
    restoreFailed: 'Das Wiederherstellen hat nicht geklappt.',
    close: 'Schließen',
    // Eine Zeile je Eintrag: „vor 3 Min. · Automatisch · 48 KB".
    meta: '{time} · {origin} · {size}',
    origin: {
      autosave: 'Automatisch',
      manual: 'Manuell gesichert',
      before_restore: 'Vor dem Wiederherstellen',
      retrace: 'Vor dem Vektorisieren',
      import: 'Vor dem Einfügen',
    },
    time: {
      justNow: 'gerade eben',
      minutes: { one: 'vor 1 Min.', other: 'vor {count} Min.' },
      hours: { one: 'vor 1 Std.', other: 'vor {count} Std.' },
      days: { one: 'vor 1 Tag', other: 'vor {count} Tagen' },
    },
  },

  cutReport: {
    title: 'Hat der Schnitt geklappt?',
    serverFallback: 'Das hat nicht geklappt. Versuch es gleich noch einmal.',
    devicesFailed: 'Die Geräteliste ließ sich nicht laden. Lad die Seite noch einmal.',
    thanks: 'Danke — dein Schnittbericht hilft der Nächsten.',
    view: 'Ansehen',
    introTemplate: 'Dein Bericht zu „{title}“ — er steht danach bei der Vorlage und bei deinem Gerät.',
    intro: 'Sag den anderen, womit es geklappt hat. Aus vielen Berichten werden die Geräteseiten.',
    existing: { one: 'Zu diesem Projekt hast du schon 1 Bericht geschrieben.', other: 'Zu diesem Projekt hast du schon {count} Berichte geschrieben.' },
    device: 'Gerät',
    pleaseChoose: 'Bitte wählen',
    material: 'Material',
    cutterSettings: 'Einstellungen an der Schneidemaschine',
    blade: 'Klinge',
    pressure: 'Anpressdruck',
    preset: 'Materialvoreinstellung',
    laserSettings: 'Einstellungen am Laser',
    power: 'Leistung (%)',
    speed: 'Geschwindigkeit (mm/s)',
    passes: 'Durchgänge',
    outcomeLegend: 'Und, hat es geklappt?',
    exportedWith: 'Womit exportiert?',
    notes: 'Notizen (optional)',
    photo: 'Foto vom Ergebnis (optional)',
    photoHint: 'Das Foto wird öffentlich bei Gerät und Vorlage gezeigt; Metadaten (Ort, Kamera) entfernen wir.',
    cancel: 'Abbrechen',
    sending: 'Einen Moment…',
    submit: 'Bericht speichern',
    outcomeGood: 'Sauber',
    outcomeGoodHint: 'Beim ersten Versuch durch.',
    outcomeAdjusted: 'Nachjustiert',
    outcomeAdjustedHint: 'Ging nach ein paar Änderungen.',
    outcomeFailed: 'Fehlschnitt',
    outcomeFailedHint: 'Hat nicht geschnitten.',
    profileCricut: 'Cricut',
    profileXtool: 'xTool',
    profileOther: 'Anderes Programm',
    myDevices: 'Meine Geräte',
    validation: {
      integerRange: '{label}: eine ganze Zahl zwischen {min} und {max}.',
      device: 'Sag uns, mit welchem Gerät du geschnitten hast.',
      material: 'Ohne Material hilft der Bericht niemandem.',
      maxLength: 'Höchstens {max} Zeichen.',
      outcome: 'Hat der Schnitt geklappt?',
      power: 'Leistung: eine Zahl zwischen 0 und 100.',
      speed: 'Geschwindigkeit: eine Zahl über 0.',
    },
    errors: {
      invalid: 'Das Formular ist noch nicht vollständig.',
      not_found: 'Das gibt es nicht mehr.',
      too_many_requests: 'Zu viele Berichte in kurzer Zeit — versuch es später noch einmal.',
    },
  },

  // Kontexthilfe: das kleine „?" im Kopf jedes Panels, ein Modal mit zwei bis
  // vier Sätzen. `button` ist das aria-label des Knopfs selbst; die übrigen
  // Paare sind Titel und Text des Modals, je Panel.
  help: {
    button: 'Hilfe zu {title}',
    layersTitle: 'Ebenen',
    layersBody:
      'Eine Ebene ist eine Farbe im Dokument, in der Liste in Zeichenreihenfolge gestapelt — die oberste Zeile liegt hinten, die unterste vorn. Jede Ebene trägt eine Rolle: Gravur (Füllung) oder Schnitt (Kontur). Ebenen lassen sich umbenennen, gruppieren und zusammenführen. Zwei direkt benachbarte Ebenen gleicher Farbe sind eine: entsteht so ein Paar beim Umsortieren oder Umfärben, verbindet der Editor es im selben Schritt; „Gleichfarbige Nachbarn verbinden" holt das für ein Dokument nach, das schon so ankam. Bekommt eine Ebene die Farbe einer anderen, die nicht neben ihr liegt, bietet der Editor an, beide zu verschmelzen, statt es still zu tun.',
    colorsTitle: 'Farben',
    colorsBody:
      'Eine Kachel je Farbe, die im Dokument vorkommt. „Ähnliche zusammenführen" fasst mit dem Regler nahe beieinanderliegende Farben zu einer Ebene zusammen — praktisch, wenn eine Vektorisierung mehrere Beinahe-Duplikate einer Farbe erzeugt hat. „Nur umfärben" übernimmt die Zielfarbe für alle betroffenen Ebenen, ohne sie zu verschmelzen.',
    pathTitle: 'Pfad',
    pathBody:
      '„Vereinfachen" senkt die Knotenzahl des gewählten Pfads über einen Toleranz-Regler und zeigt die Wirkung als Vorschau auf der Zeichenfläche, bevor sie übernommen wird — bleibt eine Anpassung innerhalb der Toleranz, ändert sich am Ergebnis nichts. „Glätten" macht die Übergänge des Pfads über einen Stärke-Regler weicher — die Knoten bleiben liegen, nur ihre Rundungen wachsen mit; auch hier zeigt die Zeichenfläche eine Vorschau, und erst das Loslassen übernimmt sie.',
    arrangeTitle: 'Anordnen',
    arrangeBody:
      'Ausrichten braucht mindestens zwei Pfade oder wirkt gegen die Seite, Verteilen mindestens drei. „Reihenfolge" schiebt die Auswahl im Stapel: ganz nach vorn oder hinten innerhalb der eigenen Ebene, eine Stufe wechselt am Ebenenrand in die Nachbarebene — und damit deren Farbe. Maße und Drehung wirken direkt auf die Auswahl, „Vervielfältigen" wiederholt sie im Kreis oder im Raster. Lineale, Raster und Fang darunter sind reine Ansichtshilfen — sie stehen nicht im Dokument und erzeugen keinen Rückgängig-Schritt.',
    cleanupTitle: 'Aufräumen',
    cleanupBody:
      'Findet Kleinteile unter einer einstellbaren Kantenlänge, Knoten über dem Budget, das ein Cutter noch zuverlässig liest, und lose Teile, die beim Schneiden als eigene Schablone herausfallen würden. Entfernen, Optimieren und Verschmelzen wirken jeweils auf die sichtbaren Ebenen (bzw. nur die solo geschaltete) und sind ein eigener Rückgängig-Schritt.',
    shapeTitle: 'Form',
    shapeBody:
      '„Vereinen", „Abziehen", „Schneiden", „Ausschließen" und „Teilen" verrechnen mehrere ausgewählte Flächen booleisch — offene Pfade nehmen daran nicht teil. „Kontur…" versetzt die Auswahl um einen Abstand nach außen oder innen, wahlweise als neue Ebene unter der Quelle oder als Ersatz der Pfade selbst.',
    rhinestoneTitle: 'Strass',
    rhinestoneBody:
      'Macht aus der Auswahl ein Lochmuster für Strass-Steine: Kreise entlang der Kontur (Ecken zuerst), als Gitter in der Fläche oder beides. Die Kreise sind die Löcher der Schablone — Steingröße plus Spiel —, und kein Loch überlappt ein anderes. „Anwenden" legt eine neue Schnitt-Ebene „Strass" an; die Quelle bleibt erhalten.',
    sheetTitle: 'Bogen',
    sheetBody:
      'Packt die Auswahl — oder alle sichtbaren Pfade, wenn nichts markiert ist — platzsparend auf eine Mattengröße, mit optionaler 90°-Drehung. „Kopien füllen" vervielfältigt ein Motiv, bis die Matte voll ist oder eine gewählte Anzahl erreicht ist; beides landet zusammen mit der neuen Seitengröße in einem einzigen Rückgängig-Schritt.',
    rasterTitle: 'Bitmap',
    rasterBody:
      'Pinsel und Radierer malen wahlweise auf der aktiven Ebene (als Vektorform) oder auf dem Foto (als Pixel) — der Schalter „Malen auf" entscheidet. Die Smart-Füllung flutet einen zusammenhängenden oder gleichfarbigen Bildbereich und macht daraus eine Kontur in der aktiven Farbe. Ist das Zuschneiden-Werkzeug aktiv, zeigt dieses Panel auch die Maße des Zuschnitts.',
    aiTitle: 'KI',
    aiBody:
      'Entfernt den Hintergrund oder verdoppelt die Auflösung des Bilds — beides läuft im Browser, ohne Server, und ist Pro-exklusiv. Das Ergebnis ersetzt das Arbeitsbild als ein Rückgängig-Schritt; „… und neu vektorisieren" führt die Vektorisierung im selben Zug gleich mit aus.',
    retraceTitle: 'Neu vektorisieren',
    retraceBody:
      'Die Regler wirken zunächst nur auf eine kostenlose Vorschau — erst „Neu vektorisieren" ersetzt die Vektor-Ebenen wirklich und kostet ein Kontingent des Tages. „Drei Varianten rechnen" vergleicht halbe, gleiche und doppelte Farbzahl nebeneinander; „Bereich nachbessern" vektorisiert nur einen gezogenen Ausschnitt neu statt des ganzen Bilds.',
    referenceTitle: 'Referenz',
    referenceBody:
      'Legt das Originalfoto als durchsichtige Ebene unter die Vektorformen, damit sich die Kontur mit dem Bild vergleichen lässt. Der Regler bestimmt die Deckkraft — 0 % blendet das Originalbild vollständig aus. Die Produkt-Mockups darunter zeigen stattdessen die Silhouette eines Bechers, T-Shirts, einer Klappkarte oder eines Glases in echter Größe, damit sich die Dokumentgröße daran ausrichten lässt.',
    exportTitle: 'Export',
    exportBody:
      'Wähle ein Geräteprofil (Cricut, xTool, Brother ScanNCut) und ein dazu passendes Dateiformat — SVG, DXF, PNG, PDF oder FCM. „Überlappungen vor dem Export verschmelzen" vereint überlappende Formen in jeder sichtbaren Ebene, damit nichts doppelt geschnitten wird; danach lässt sich direkt ein Schnittbericht schreiben.',
    layeringTitle: 'Schichtung',
    layeringBody:
      '„Überfüllen" lässt jede Ebene um die eingestellte Überlappung unter die Ebenen darüber wachsen — so blitzt zwischen zwei nebeneinander liegenden Folienfarben kein Trägermaterial durch, wenn beim Aufbügeln ein Zehntelmillimeter verrutscht. „Ausstanzen" macht das Gegenteil und nimmt aus jeder Ebene heraus, was die Ebenen darüber ohnehin verdecken, bis auf denselben schmalen Saum am Rand — das spart Material und lässt den Stapel nicht dick auftragen.',
    historyTitle: 'Verlauf',
    historyBody:
      'Zeigt gespeicherte Versionen des Dokuments mit Zeitpunkt und Vorschau — das Arbeitsbild wird dabei nicht versioniert. Eine Version lässt sich ansehen und wiederherstellen, ohne die aktuelle Arbeit zu verlieren: Wiederherstellen sichert den jetzigen Stand vorher selbst als Version.',
  },

  // Parametrische Muster: geometry/patterns/*, PatternPanel.tsx.
  patterns: {
    title: 'Muster',
    rectHint: 'Ohne Auswahl entsteht ein Rechteck aus Breite und Höhe, mittig auf dem Blatt.',
    kind: 'Muster',
    kindHoneycomb: 'Wabe',
    kindVoronoi: 'Voronoi',
    kindSpirograph: 'Spirograph',
    kindHoles: 'Zufalls-Löcher',
    width: 'Breite',
    widthLabel: 'Breite des Rechtecks in Millimetern',
    height: 'Höhe',
    heightLabel: 'Höhe des Rechtecks in Millimetern',
    cellSize: 'Zellgröße',
    cellSizeLabel: 'Schlüsselweite der Wabenzelle in Millimetern',
    wall: 'Wandstärke',
    wallLabel: 'Wandstärke zwischen den Wabenzellen in Millimetern',
    cellCount: 'Zellen',
    cellCountLabel: 'Zahl der Voronoi-Zellen',
    web: 'Mindeststeg',
    webLabel: 'Mindeststeg zwischen den Löchern in Millimetern',
    seed: 'Zufalls-Seed',
    seedLabel: 'Zufalls-Seed des Musters',
    reroll: 'Neu würfeln',
    relax: 'Entspannt (gleichmäßigere Zellen)',
    bigRadius: 'Großer Kreis R',
    bigRadiusLabel: 'Radius des festen Kreises in Millimetern',
    smallRadius: 'Kleiner Kreis r',
    smallRadiusLabel: 'Radius des rollenden Kreises in Millimetern',
    penOffset: 'Stiftabstand d',
    penOffsetLabel: 'Abstand des Stifts von der Mitte des rollenden Kreises in Millimetern',
    turns: 'Umläufe',
    turnsLabel: 'Zahl der Umläufe',
    spirographNote:
      'Der Spirograph ist eine Linie, kein Loch: er landet immer in einer eigenen Ebene mit Rolle Schnitt — für Stiftplotter oder Gravur.',
    radiusMin: 'Radius min',
    radiusMinLabel: 'Kleinster Lochradius in Millimetern',
    radiusMax: 'Radius max',
    radiusMaxLabel: 'Größter Lochradius in Millimetern',
    density: 'Dichte (%)',
    densityLabel: 'Angestrebter Flächenanteil der Löcher in Prozent',
    output: 'Ergebnis',
    outputHoles: 'Als Löcher in der Form',
    outputLayer: 'Als eigene Ebene',
    layerName: 'Muster',
    count: { one: '1 Zelle', other: '{count} Zellen' },
    densityShort: '— von {wanted} angestrebten. Für mehr: kleinere Radien oder weniger Mindeststeg.',
    refusedCells: 'Zu fein: {wanted} Zellen, höchstens {limit} gehen. Zellgröße erhöhen.',
    refusedNodes:
      'Zu viel: {nodes} Knoten, höchstens {limit} gehen. Gröberes Muster — größere Zellen, weniger Löcher, weniger Umläufe.',
    budget: '{nodes} Knoten — über dem Schnittbudget von {limit}. Gröberes Muster oder „Knoten optimieren" im Aufräumen-Panel.',
    apply: 'Muster anwenden',
    empty: 'Kein Muster passt — die Form ist zu klein für diese Zellgröße und diesen Steg.',
    done: { one: 'Muster angewendet — 1 Zelle', other: 'Muster angewendet — {count} Zellen' },
    helpTitle: 'Muster',
    helpBody:
      'Füllt die gewählte geschlossene Form mit einem parametrischen Lochmuster — Wabe, Voronoi, Spirograph oder Zufalls-Löcher. Die Zellen werden mit der Form verschnitten und um den Mindeststeg nach innen versetzt, so dass zwischen allen Löchern und zum Rand genug Material stehen bleibt. „Als Löcher in der Form" schreibt die Zellen als Teilpfade in die Form selbst, „Als eigene Ebene" legt sie in eine neue Ebene „Muster".',
  },

  // „Einfügen → Monogramm…": aus ein bis drei Initialen wird ein Zeichen
  // (geometry/monogram.ts, MonogramDialog.tsx). Eine eigene Gruppe statt
  // Einträgen in `insert` und `help`, damit der Generator auch in dieser
  // Datei ein Block bleibt, der sich am Stück lesen lässt.
  monogram: {
    menu: 'Monogramm…',
    title: 'Monogramm',
    initials: 'Initialen (1–3)',
    style: 'Stil',
    styleClassic: 'Klassisch',
    styleCircle: 'Kreis',
    styleDiamond: 'Raute',
    styleSplit: 'Split-Letter',
    font: 'Schrift',
    width: 'Gesamtbreite (mm)',
    frame: 'Rahmen',
    ring: 'Rahmenbreite (mm)',
    band: 'Bandhöhe (mm)',
    bandAuto: 'automatisch (18 %)',
    bandName: 'Name im Band',
    weld: 'Buchstaben verschmelzen',
    preview: 'Vorschau des Monogramms',
    previewEmpty: 'Gib ein bis drei Initialen ein.',
    create: 'Einfügen',
    cancel: 'Abbrechen',
    layerName: 'Monogramm',
    invalidInitials: 'Bitte ein bis {max} Initialen eingeben.',
    invalidWidth: 'Bitte eine Gesamtbreite zwischen {min} und {max} mm eingeben.',
    invalidRing: 'Bitte eine Rahmenbreite zwischen {min} und {max} mm eingeben.',
    invalidBand: 'Bitte eine Bandhöhe zwischen {min} und {max} mm eingeben.',
    nothing: 'Diese Schrift hat für diese Zeichen keine Umrisse.',
    failed: 'Das Monogramm konnte nicht erzeugt werden.',
    done: 'Monogramm eingefügt — es liegt oben auf der Ebene „Monogramm".',
    hint: 'Klassisch stellt die mittlere Initiale — den Nachnamen — größer in die Mitte. Kreis und Raute verschneiden die Buchstaben mit der Form, Split-Letter stanzt ein waagerechtes Band aus einem großen Buchstaben.',
    helpTitle: 'Monogramm',
    helpBody:
      'Ein Monogramm entsteht hier als reine Pfade in einer neuen Ebene „Monogramm" — alle geschlossen, auf die eingestellte Gesamtbreite skaliert und auf Wunsch zu einer einzigen Silhouette verschmolzen. Anders als beim Text bleibt keine Anleitung an der Ebene hängen: was eingefügt ist, ist ab dann gewöhnliche Geometrie und wird mit den Werkzeugen des Editors weiterbearbeitet.',
  },

  // Ausrichtmarken für mehrlagiges Schichten („Einfügen → Ausrichtmarken…",
  // RegistrationMarksDialog.tsx, geometry/registrationMarks.ts). Nicht zu
  // verwechseln mit den Passermarken von Print & Cut: die liest die Kamera
  // des Plotters, diese hier liest das Auge.
  registrationMarks: {
    menu: 'Ausrichtmarken…',
    title: 'Ausrichtmarken',
    shape: 'Form',
    shapeTriangle: 'Dreieck',
    shapeCross: 'Kreuz',
    size: 'Größe (mm)',
    gap: 'Abstand zum Motiv (mm)',
    count: 'Positionen',
    count2: '2 — oben links, unten rechts',
    count3: '3 Ecken',
    count4: '4 Ecken',
    target: 'Wohin',
    targetEachLayer: 'In jede Farbebene',
    targetOwnLayer: 'Als eigene Ebene',
    layerName: 'Ausrichtmarken',
    hint: 'Setze die Marken zuletzt — nach „Anordnen" und „Nach Farben ordnen". Der Bogen-Packer lässt sie zwar liegen, aber das Motiv wandert darunter weg.',
    invalidSize: 'Die Größe muss zwischen {min} und {max} mm liegen.',
    invalidGap: 'Der Abstand muss zwischen {min} und {max} mm liegen.',
    empty: 'Keine sichtbare Ebene mit Inhalt — es gibt nichts, worum die Marken herum könnten.',
    cancel: 'Abbrechen',
    create: 'Einfügen',
    remove: 'Ausrichtmarken entfernen',
    done: {
      one: '1 Ausrichtmarke gesetzt.',
      other: '{count} Ausrichtmarken gesetzt.',
    },
    removed: {
      one: '1 Ausrichtmarke entfernt.',
      other: '{count} Ausrichtmarken entfernt.',
    },
    helpTitle: 'Ausrichtmarken',
    helpBody:
      'Ausrichtmarken sind kleine Dreiecke oder Kreuze, die in jeder Farbebene an genau derselben Stelle liegen — wer die geschnittenen Lagen Marke auf Marke legt, hat damit auch das Motiv passgenau übereinander. Ein erneutes Einfügen ersetzt die vorhandenen Marken, und „Ausrichtmarken entfernen" nimmt sie aus allen Ebenen wieder heraus, auch nachdem sie verschoben wurden.',
  },

  // „Einfügen → Zinkenbox…": eine Steckkiste für den Laser
  // (geometry/fingerJointBox.ts). Nicht zu verwechseln mit „Schachtel…" — die
  // ist aus Papier und wird gefalzt, diese hier aus Holz oder Acryl und wird
  // zusammengesteckt. Deshalb Materialstärke, Kerf und gegengleiche Kanten
  // statt Falzlinien.
  fingerJoint: {
    menu: 'Zinkenbox…',
    title: 'Zinkenbox',
    length: 'Länge (mm)',
    width: 'Breite (mm)',
    height: 'Höhe (mm)',
    measure: 'Maße sind',
    measureOuter: 'Außenmaß',
    measureInner: 'Innenmaß',
    thickness: 'Materialstärke (mm)',
    finger: 'Zinkenbreite (mm)',
    fingerAuto: 'automatisch (doppelte Materialstärke)',
    lid: 'Deckel',
    lidClosed: 'geschlossen (6 Teile)',
    lidOpen: 'offen (5 Teile)',
    kerf: 'Kerf (mm)',
    summary: 'Außen {length} × {width} × {height} mm · {parts} Teile · {fingers} Zinken je Längskante · Bogen {sheetWidth} × {sheetHeight} mm',
    create: 'Anlegen',
    cancel: 'Abbrechen',
    layerName: 'Zinkenbox',
    invalidSize: 'Bitte Maße zwischen {min} und {max} mm eingeben.',
    invalidThickness: 'Bitte eine Materialstärke zwischen {min} und {max} mm eingeben.',
    invalidFinger: 'Bitte eine Zinkenbreite zwischen {min} und {max} mm eingeben — oder das Feld für „automatisch" leer lassen.',
    invalidKerf: 'Bitte einen Kerf zwischen 0 und {max} mm eingeben.',
    tooFlat: 'Zu flach: Boden und Deckel lassen keine Höhe für die Zinken übrig. Höher machen oder dünneres Material wählen.',
    tooFine: 'Die Zinken wären schmaler als das Material dick ist — sie brechen beim Zusammenstecken. Breitere Zinken wählen.',
    done: 'Zinkenbox angelegt — {parts} Teile auf einer Linien-Ebene.',
    hint: 'Für den Laser: alle Teile liegen flach nebeneinander, die Kanten sind gegengleich und werden zusammengesteckt, nicht geklebt. Der Kerf gleicht aus, was der Strahl wegbrennt — bei 3 mm Sperrholz meist 0,1 bis 0,2 mm. Zum Falzen aus Papier ist „Schachtel…" gedacht.',
  },

  // „Einfügen → Biegeschlitze…" (Living Hinge): ein Feld kurzer Schnitte, das
  // eine steife Platte an einer Stelle biegsam macht (geometry/livingHinge.ts).
  livingHinge: {
    menu: 'Biegeschlitze…',
    title: 'Biegeschlitze',
    width: 'Feldbreite (mm)',
    height: 'Feldhöhe (mm)',
    slot: 'Schlitzlänge (mm)',
    bridge: 'Steg (mm)',
    rowGap: 'Reihenabstand (mm)',
    stagger: 'Versetzt (Ziegelmuster)',
    frame: 'Rahmen um das Feld',
    summary: '{rows} Reihen · {slots} Schlitze · Feld {width} × {height} mm',
    create: 'Anlegen',
    cancel: 'Abbrechen',
    layerName: 'Biegeschlitze',
    invalidField: 'Bitte eine Feldgröße zwischen {min} und {max} mm eingeben.',
    invalidSlot: 'Bitte eine Schlitzlänge zwischen {min} und {max} mm eingeben.',
    invalidBridge: 'Bitte einen Steg zwischen {min} und {max} mm eingeben.',
    invalidRowGap: 'Bitte einen Reihenabstand zwischen {min} und {max} mm eingeben.',
    tooSmall: 'In dieses Feld passt kein einziger Schlitz: Schlitz und Steg sind zusammen höher als das Feld.',
    done: '{slots} Biegeschlitze angelegt — auf einer Linien-Ebene.',
    hint: 'Die Schlitze laufen senkrecht; gebogen wird um die senkrechte Achse. Je kürzer der Steg und je enger die Reihen, desto weicher der Bogen — und desto eher bricht er. Versetzte Reihen verteilen die Verdrehung auf doppelt so viele Stege. Kein Schlitz berührt den Rand.',
  },

  // „Einfügen → Schichtbild…": ein Motiv in n Papierlagen zerlegt, die
  // hintereinander im Rahmen stehen (raster/shadowbox.ts für die Bildlogik,
  // geometry/shadowbox.ts für den Zuschnitt). Eigene Gruppe statt eines
  // Eintrags unter `insert`, damit dieses Werkzeug seine Texte an einer
  // Stelle hat — vom Menüeintrag bis zur Kontexthilfe.
  shadowbox: {
    menu: 'Schichtbild…',
    title: 'Schichtbild',
    layers: 'Lagen',
    frame: 'Rahmenbreite (mm)',
    web: 'Mindeststeg (mm)',
    cumulative: 'Kumulativ (Relief statt einzelner Bänder)',
    preview: 'Vorschau',
    previewPending: 'Wird berechnet …',
    previewEmpty: 'Noch nichts zu zeigen.',
    previewCaption: 'Die Lagen von vorne nach hinten, gestapelt.',
    create: 'Einfügen',
    cancel: 'Abbrechen',
    layerFront: 'Lage {index} (vorne)',
    layerMiddle: 'Lage {index}',
    layerBack: 'Lage {index} (hinten)',
    invalidLayers: 'Bitte eine Lagenzahl zwischen {min} und {max} eingeben.',
    invalidFrame: 'Bitte eine Rahmenbreite zwischen {min} und {max} mm eingeben.',
    invalidWeb: 'Bitte einen Mindeststeg zwischen {min} und {max} mm eingeben.',
    noImage: 'Dieses Projekt hat kein Arbeitsbild — ein Schichtbild wird aus dem Bild gerechnet.',
    failed: 'Das Schichtbild konnte nicht berechnet werden.',
    done: '{layers} Lagen eingefügt — „Lage 1" liegt vorne.',
    doneIslands:
      '{layers} Lagen eingefügt; {islands} lose Teile ohne Verbindung zum Rahmen wurden weggelassen.',
    helpTitle: 'Schichtbild',
    helpBody:
      'Zerlegt das Arbeitsbild nach Helligkeit in mehrere Papierlagen: jede Lage ist der Rahmen mit den Fenstern, durch die man auf die nächste schaut, und weil dunkle Stellen in mehr Lagen offen sind, entsteht ein Relief. Die Schwellen liegen auf den Quantilen der Helligkeit, damit jede Lage ungefähr gleich viel öffnet; Teile, die am Rahmen nicht mehr hängen, fallen weg und werden gezählt. Tipp: die fertigen Lagen mit 2–3 mm Abstandshaltern hintereinander montieren — ohne Abstand gibt es keinen Schatten und damit keine Tiefe.',
  },

  // „Einfügen → Graustufen für Lasergravur…" (GrayscaleDialog.tsx): die ganze
  // Zeichenfläche als Graustufen-Bild, als PNG mit DPI oder als Arbeitsbild.
  grayscale: {
    menu: 'Graustufen für Lasergravur…',
    exportLink: 'Graustufen für Lasergravur …',
    exportHint: 'Foto und Ebenen als ein Graustufen-PNG für die Rastergravur.',
    title: 'Graustufen für Lasergravur',
    preview: 'Vorschau der Gravur',
    previewPending: 'Wird berechnet …',
    previewFailed: 'Die Zeichenfläche konnte nicht dargestellt werden.',
    brightness: 'Helligkeit',
    contrast: 'Kontrast',
    gamma: 'Gamma',
    invert: 'Invertieren (für Glas, Schiefer, eloxiertes Alu)',
    removeBackground: 'Hintergrund entfernen (Motiv per KI freistellen, Schrift bleibt, PNG transparent)',
    removingBackground: 'Hintergrund wird entfernt … {progress}',
    removeBackgroundFailed: 'Der Hintergrund konnte nicht entfernt werden — das Bild bleibt vollständig, nur leere Stellen sind transparent.',
    reset: 'Zurücksetzen',
    dpi: 'Auflösung',
    dpiOption: '{dpi} DPI · {width} × {height} px',
    dpiTooLarge: '{dpi} DPI · zu groß für den Browser',
    download: 'Als PNG herunterladen',
    apply: 'Als Arbeitsbild übernehmen',
    proOnly: 'Herunterladen und Übernehmen gehören zu Pro. Die Vorschau kannst du ausprobieren.',
    noImage: 'Dieses Projekt hat kein Arbeitsbild — das Graustufen-Bild lässt sich nur herunterladen.',
    otherArtboard: 'Das Arbeitsbild gehört zur ersten Zeichenfläche — übernehmen geht nur dort, herunterladen überall.',
    downloaded: 'Graustufen-PNG mit {dpi} DPI gespeichert.',
    applied: 'Graustufen-Bild ist jetzt das Arbeitsbild; die Ebenen sind ausgeblendet.',
    failed: 'Das Graustufen-Bild konnte nicht berechnet werden.',
    helpTitle: 'Graustufen für Lasergravur',
    helpBody:
      'Setzt das Arbeitsbild und alle sichtbaren Ebenen der aktiven Zeichenfläche zu einem Graustufen-Bild zusammen. Ein Laser liest den Grauwert als Leistung: Schwarz brennt voll, Weiß gar nicht — leere Stellen bleiben deshalb weiß. Mit Helligkeit, Kontrast und Gamma stellst du ein, wie dunkel die Gravur auf deinem Material wird; „Invertieren" brauchst du für Glas, Schiefer oder eloxiertes Aluminium, wo das Lasern hell statt dunkel macht. Das PNG trägt die gewählte Auflösung in sich, LightBurn und xTool Creative Space übernehmen damit die Größe in Millimetern. 318 DPI (≈ 0,08 mm Zeilenabstand) ist ein guter Start für Holz. „Als Arbeitsbild übernehmen" ersetzt das Arbeitsbild und blendet die Ebenen aus — rückgängig machen geht mit einem Schritt.',
  },

  // Schriften, die am Konto hängen statt nur an diesem Gerät (accountFonts.ts).
  // Der Gegenpart zu `customFonts` darüber: dort bleibt die Datei im Browser,
  // hier wurde sie auf /konto ausdrücklich hochgeladen, damit sie auf jedem
  // angemeldeten Gerät zur Verfügung steht.
  accountFonts: {
    group: 'Aus deinem Konto',
    badge: 'Konto',
    loading: 'Schrift wird aus deinem Konto geholt…',
    notUsable: 'WOFF2 — nicht als Pfade',
    manage: 'Schriften im Konto verwalten',
    // „für diese Sitzung", nicht „auf diesem Gerät": eine Kontoschrift wird
    // bewusst nicht in den Gerätespeicher gelegt (CustomFontsSection.tsx).
    added: 'Schrift „{font}" aus deinem Konto geladen.',
    // Offline ist im Editor der Normalfall und kein Absturz: die Pfade eines
    // schon gesetzten Textes stehen weiter, nur neu setzen geht gerade nicht.
    errorOffline:
      'Die Schrift „{font}" liegt in deinem Konto, aber du bist gerade offline. Sobald du wieder Netz hast, geht es.',
    errorDenied: 'Für „{font}" fehlt die Anmeldung — melde dich neu an und versuch es noch einmal.',
    errorGone: 'Die Schrift „{font}" ist nicht mehr in deinem Konto.',
    errorMismatch: 'Die Schrift „{font}" kam beschädigt an und wurde nicht gespeichert.',
    errorUnreadable: 'Die Schrift „{font}" ließ sich gerade nicht aus deinem Konto holen.',
    // Der Weg zurück (uploadAccountFont): mit Pro geht eine im Textdialog
    // hinzugefügte Schrift von selbst ins Konto. Scheitert das, liegt sie
    // trotzdem auf dem Gerät -- jeder dieser Sätze sagt das mit, damit der
    // gelungene Teil nicht wie ein Fehlschlag aussieht.
    stored: {
      one: '1 Schrift hinzugefügt und in deinem Konto abgelegt — sie steht dir in jedem Projekt bereit.',
      other: '{count} Schriften hinzugefügt und in deinem Konto abgelegt — sie stehen dir in jedem Projekt bereit.',
    },
    uploadOffline:
      '„{font}" liegt auf diesem Gerät, aber du bist offline — ins Konto kommt sie erst, wenn du sie mit Netz noch einmal hinzufügst oder unter „Konto → Eigene Schriften" hochlädst.',
    uploadDenied:
      '„{font}" liegt auf diesem Gerät, kam aber nicht ins Konto — melde dich neu an und füge sie noch einmal hinzu.',
    uploadThrottled:
      '„{font}" liegt auf diesem Gerät, kam aber nicht ins Konto: zu viele Uploads in dieser Stunde. Später noch einmal hinzufügen oder unter „Konto → Eigene Schriften" hochladen.',
    uploadRejected: '„{font}" liegt auf diesem Gerät, kam aber gerade nicht ins Konto.',
    uploadRejectedWhy: '„{font}" liegt auf diesem Gerät, kam aber nicht ins Konto: {detail}',
  },

  // Eigene Schriften (customFonts.ts, CustomFontsSection.tsx). Eine eigene
  // Gruppe, weil die Schriftdatei ohne Pro nie den Browser verlässt und das
  // Feature deshalb überall von „einer Schrift wählen" erzählen muss, ohne
  // dem Server je etwas zu schicken -- und mit Pro genau das Gegenteil
  // vorher ansagen muss (`licenseAccount`).
  customFonts: {
    group: 'Eigene',
    add: 'Eigene Schrift hinzufügen…',
    reading: 'Schrift wird gelesen…',
    formats: 'TTF, OTF oder WOFF — WOFF2 kann der Editor nicht lesen. Höchstens 5 MB je Datei, mehrere auf einmal gehen.',
    license:
      'Nur Schriften verwenden, deren Lizenz das Erstellen von Schnittdateien erlaubt — Schnittklar prüft keine Lizenzen. Hier hinzugefügt, bleibt die Datei auf diesem Gerät.',
    // Die Fassung für ein Pro-Konto: dass die Datei hochgeladen wird, muss
    // *vor* dem Hinzufügen dastehen, nicht erst im Toast danach.
    licenseAccount:
      'Nur Schriften verwenden, deren Lizenz das Erstellen von Schnittdateien erlaubt — Schnittklar prüft keine Lizenzen. Hier hinzugefügt, landet die Datei in deinem Konto und steht dir in jedem Projekt und auf jedem Gerät bereit, auf dem du angemeldet bist.',
    remove: '{font} entfernen',
    removed: '{font} entfernt',
    added: { one: '1 Schrift hinzugefügt', other: '{count} Schriften hinzugefügt' },
    // Genannt wird die Schrift mit dem Namen, den das Rezept mitbringt
    // (`custom:<hash>:<Name>`); nur ein Rezept aus der ersten Fassung hat
    // keinen und muss mit den ersten acht Hexstellen auskommen.
    missing:
      'Die Schrift „{font}" liegt nicht auf diesem Gerät. Füge sie hinzu — mit Pro landet sie dabei in deinem Konto und ist auf jedem Gerät da — oder wähle eine andere.',
    missingOption: 'Nicht auf diesem Gerät',
    errorTooLarge: '„{file}" ist zu groß — höchstens {max} MB je Schriftdatei.',
    errorWoff2: '„{file}" ist eine WOFF2-Datei. Die kann der Editor nicht lesen — nimm TTF, OTF oder WOFF.',
    errorUnsupported: '„{file}" ist keine Schriftdatei, die der Editor lesen kann — nimm TTF, OTF oder WOFF.',
    errorUnreadable: '„{file}" konnte nicht gelesen werden — die Datei ist beschädigt oder unvollständig.',
    errorStore: '„{file}" konnte auf diesem Gerät nicht gespeichert werden.',
    helpTitle: 'Eigene Schriften',
    helpBody:
      'Hier hinzugefügte Schriftdateien werden im Browser gelesen und auf diesem Gerät gespeichert. Mit Pro legt der Editor jede hinzugefügte Schrift zusätzlich in dein Konto — beliebig viele, höchstens 5 MB je Datei: der Textdialog bietet sie dann in jedem Projekt und auf jedem Gerät an, auf dem du angemeldet bist, und die Datei geht an niemanden sonst, auch nicht, wenn du das Projekt veröffentlichst. Ohne Pro bleibt die Datei auf diesem Gerät. Bei jemandem, der deine Vorlage öffnet, bleiben die gesetzten Buchstaben als Pfade erhalten; nur neu setzen lässt sich der Text dort erst mit einer eigenen Schrift.',
  },

  // Schnittspalt (Kerf) im Export-Panel: die Kompensation, die ein Laser
  // braucht, damit die Teile die Größe haben, in der sie gezeichnet wurden.
  kerf: {
    label: 'Schnittspalt (Kerf)',
    hint: 'Typisch 0,1–0,2 mm bei Lasern; bei Messerplottern 0 lassen. Außenkonturen wachsen um die Hälfte, Löcher schrumpfen um die Hälfte; offene Pfade bleiben. Trennt das Dokument Schnitt und Gravur, wird nur der Schnitt kompensiert.',
    invalid: 'Bitte eine Zahl zwischen 0 und 1 mm eingeben.',
    zipHint: 'Gilt für den Datei-Export oben; die ZIP-Exporte und Print & Cut gehen ohne Kompensation heraus.',
    badge: 'Kerf {mm} angewendet.',
    historyLabel: 'Kerf {mm}',
  },

  // Die persönliche Elementbibliothek: eine Auswahl unter einem Namen
  // aufheben und sie in jedem eigenen Projekt wieder einsetzen.
  library: {
    menu: 'Bibliothek…',
    title: 'Elementbibliothek',
    save: 'In Bibliothek speichern',
    saveTitle: 'In Bibliothek speichern',
    saveLabel: 'Name',
    saveConfirm: 'Speichern',
    saved: '„{name}" liegt jetzt in deiner Bibliothek.',
    saveEmpty: 'Wähle erst aus, was gespeichert werden soll.',
    saveNoName: 'Ohne Namen findest du es nicht wieder — gib einen ein.',
    saveFailed: 'Das Element ließ sich nicht speichern.',
    saveLimit: 'Deine Bibliothek ist voll ({limit} Elemente). Lösch eines, um Platz zu machen.',
    search: 'Elemente durchsuchen…',
    loading: 'Bibliothek wird geladen…',
    loadFailed: 'Die Bibliothek ließ sich nicht laden.',
    offline: 'Du bist offline — deine Bibliothek liegt auf dem Server und ist gerade nicht erreichbar.',
    empty: 'Noch keine Elemente. Wähle etwas aus und speichere es über „In Bibliothek speichern".',
    noMatches: 'Kein Element passt zu deiner Suche.',
    size: '{width} × {height} mm',
    rename: 'Umbenennen',
    renameTitle: 'Element umbenennen',
    renameLabel: 'Name',
    renameConfirm: 'Umbenennen',
    renameFailed: 'Das Umbenennen hat nicht geklappt.',
    delete: 'Löschen',
    deleteTitle: '„{name}" löschen?',
    deleteBody: 'Das Element verschwindet aus deiner Bibliothek. Schon eingefügte Kopien bleiben, wo sie sind.',
    deleteConfirm: 'Löschen',
    deleted: 'Element gelöscht.',
    deleteFailed: 'Das Löschen hat nicht geklappt.',
    nothing: 'Dieses Element hat keine Pfade.',
    done: { one: '1 Pfad eingefügt.', other: '{count} Pfade eingefügt.' },
    undoLabel: 'Element „{name}" einfügen',
    close: 'Schließen',
    helpTitle: 'Elementbibliothek',
    helpBody:
      'Hier liegen Ausschnitte, die du dir aufgehoben hast: eine Auswahl wird über „In Bibliothek speichern" im Anordnen-Panel zu einem Element mit Name, Maßen und Vorschau. Ein Klick auf eine Kachel setzt das Element in der Mitte der Ansicht ein — farb- und rollengleiche Ebenen wandern dabei in die vorhandene Ebene, alles andere kommt als neue dazu.',
  },
  // Ausprobieren ohne Konto (GuestController, docs/BRAND.md „Sprache und
  // Preise": der Trichter der Landingpage ist zweisprachig, die Community
  // bleibt deutsch). Der Ton ist der der Marke: du, warm, ohne Superlative --
  // und der Hinweis auf ein Konto ist ein Satz, kein Verkaufsgespräch.
  guest: {
    pick: 'Bild auswählen',
    dropHint: 'Zieh ein Bild hierher oder wähl eines aus — PNG, JPG, HEIC oder PDF, höchstens 20 MB.',
    tracing: 'Wird vektorisiert …',
    tracingHint: 'Das dauert ein paar Sekunden. Lass die Seite offen.',
    resultHeading: 'Fertig',
    layers: { one: '1 Ebene', other: '{count} Ebenen' },
    size: '{width} × {height} mm',
    download: 'SVG herunterladen',
    downloadHint: 'Das SVG öffnet sich in Design Space, Silhouette Studio (Designer Edition) und Inkscape.',
    again: 'Anderes Bild versuchen',
    empty: 'Aus diesem Bild ist keine Fläche geworden. Ein Motiv mit klaren Farben und wenig Hintergrund klappt besser.',
    lost: 'Hier liegt gerade kein Ergebnis. Lade ein Bild hoch, dann ist es da.',
    // Der Hinweis nach dem Ergebnis. Ruhig, konkret, ein Satz je Sache --
    // keine Superlative, keine Dringlichkeit, kein Countdown.
    accountHeading: 'Damit weiterarbeiten',
    accountBody:
      'Dein Ergebnis liegt in diesem Browser. Mit einem Konto zieht es beim ersten Anmelden von selbst hinüber — samt Bild — und dann geht dort weiter, was hier aufhört: Ebenen zusammenlegen, Pfade glätten, neu vektorisieren, und fünf Dateien am Tag statt einer.',
    accountExtras: 'Pro bringt dazu DXF, PNG, PDF und FCM, größere Uploads und Stapelverarbeitung.',
    accountCta: 'Konto anlegen',
    accountSignIn: 'Ich habe schon eins',
    errors: {
      tooLarge: 'Die Datei ist zu groß — höchstens 20 MB.',
      unsupported: 'Das ist kein Bild, mit dem wir etwas anfangen können. Versuch ein PNG, JPG, HEIC oder ein PDF.',
      limit:
        'Dein kostenloser Versuch ist aufgebraucht. Mit einem Konto geht es weiter — fünf Dateien am Tag, kostenlos.',
      busy: 'Gerade ist viel los. Versuch es in einer Minute noch einmal.',
      timeout: 'Das Bild hat zu lange gebraucht. Ein kleineres oder einfacheres Motiv klappt schneller.',
      failed: 'Das hat nicht geklappt. Versuch es mit einem anderen Bild.',
      offline: 'Keine Verbindung. Sobald du wieder online bist, klappt es.',
    },
    imported: { one: 'Dein Ergebnis liegt jetzt in deinen Projekten.', other: '{count} Ergebnisse liegen jetzt in deinen Projekten.' },
  },

  // Der öffentliche Dateicheck unter /svg-pruefen (FileChecksController).
  // Deutsch wie die Seite selbst -- die englischen Schlüssel existieren, weil
  // en.ts sonst nicht typprüft, und tragen die englische Fassung derselben
  // Sätze.
  fileCheck: {
    pick: 'SVG auswählen',
    dropHint: 'Zieh deine SVG-Datei hierher oder wähl eine aus. Sie bleibt in deinem Browser — sie geht nicht zu uns.',
    reading: 'Wird gelesen …',
    clean: 'An dieser Datei ist nichts zu beanstanden. Sie sollte sauber schneiden.',
    findingsHeading: 'Das ist aufgefallen',
    repairsHeading: 'Das können wir reparieren',
    resultHeading: 'Repariert',
    download: 'Repariertes SVG herunterladen',
    again: 'Andere Datei prüfen',
    summary: '{paths} Pfade auf {layers} Ebenen',
    accountHeading: 'Weiterarbeiten statt nur reparieren',
    accountBody:
      'Mit einem Konto landet die reparierte Datei als Projekt im Editor: Ebenen zusammenlegen, Maße setzen, für dein Gerät exportieren.',
    accountCta: 'Konto anlegen',
    errors: {
      read: 'Die Datei ließ sich nicht lesen.',
      invalid: 'Das ist keine SVG-Datei, die wir lesen können.',
      empty: 'In dieser Datei ist keine Geometrie, aus der sich eine Schnittlinie bauen ließe.',
      tooManyNodes: 'Diese Datei hat mehr Knoten, als der Editor verarbeitet.',
      tooLarge: 'Die Datei ist zu groß — höchstens 20 MB.',
    },
  },

  // „Öffnen mit Schnittklar" (/datei-oeffnen, components/FileLaunch.tsx):
  // eine Datei, die aus dem Dateimanager der installierten App hereinkommt.
  // Übersetzt wie der Gast-Trichter und /scan -- wer die englische App
  // benutzt, öffnet Dateien nicht plötzlich auf Deutsch.
  fileLaunch: {
    // Niemand hat eine Datei mitgebracht: die Adresse wurde selbst aufgerufen.
    idleHeading: 'Keine Datei angekommen',
    idleInstalled:
      'Wähl im Dateimanager eine Datei aus, dann „Öffnen mit" und dann Schnittklar — sie wird hier zu einem neuen Projekt.',
    // Im Browser-Tab gibt es keine Dateizuordnung. Kein Knopf, der nichts
    // tut, sondern der Hinweis auf den Weg, der dort funktioniert.
    idleBrowser:
      'Dateien direkt zu öffnen, geht in der installierten App. Im Browser lädst du sie im Dashboard hoch — derselbe Weg, dasselbe Ergebnis.',

    uploading: '„{name}" wird hochgeladen …',
    tracing: '„{name}" wird vektorisiert …',
    tracingHint: 'Das dauert ein paar Sekunden. Lass die Seite offen.',
    done: '„{name}" ist fertig — das Projekt geht auf.',
    // Eine Mehrfachauswahl kommt als ein Start an; ein Projekt wird daraus.
    ignored: {
      one: 'Eine weitere Datei ist liegen geblieben — lade sie im Dashboard hoch.',
      other: '{count} weitere Dateien sind liegen geblieben — lade sie im Dashboard hoch.',
    },

    // Nicht angemeldet. Die Datei ist nicht verloren, und genau das ist der
    // erste Satz.
    signInHeading: 'Dafür brauchst du ein Konto',
    signInBody:
      '„{name}" wartet auf diesem Gerät. Melde dich an — danach legen wir das Projekt sofort an, und du musst die Datei nicht noch einmal suchen.',
    signInCta: 'Anmelden',
    signUpCta: 'Konto anlegen',
    signUpHint: 'Noch kein Konto? Auch dann bleibt die Datei hier liegen, bis du dich das erste Mal anmeldest.',

    // Ohne Netz. Ehrlich gesagt, nicht still verschluckt: vektorisiert wird
    // auf dem Server, und der Editor im Cache hilft dabei nicht.
    offlineHeading: 'Keine Verbindung',
    offlineBody:
      'Vektorisiert wird auf dem Server, und der ist gerade nicht erreichbar. „{name}" wartet auf diesem Gerät — sobald du wieder online bist, geht es von selbst weiter.',
    offlineWaiting: 'Wartet auf die Verbindung …',

    retry: 'Noch einmal versuchen',
    dashboard: 'Zu meinen Projekten',
    upgrade: 'Pro werden',

    errors: {
      unsupported: 'Damit können wir nichts anfangen. Schnittklar öffnet PNG, JPEG, WebP, HEIC, AVIF, PDF und EPS.',
      tooLarge: 'Die Datei ist zu groß — höchstens 20 MB.',
      limitReached: 'Tageslimit erreicht ({used} / {limit}).',
      limitReachedPlain: 'Tageslimit erreicht.',
      uploadFailed: 'Die Datei konnte nicht hochgeladen werden.',
      conversionFailed: 'Die Vektorisierung ist fehlgeschlagen. Versuch es mit einer anderen Datei.',
      lostTrack: 'Der Status der Vektorisierung ist nicht mehr abrufbar. Lade die Seite neu.',
    },
  },

  // Sammel-Operationen über mehrere Ebenen (`layerBulk.ts`). Ein Lauf, ein
  // Schritt im Verlauf; was nicht ging, wird gezählt und gesagt.
  layerBulk: {
    title: 'Mehrere Ebenen',
    hint: 'Mit Strg- oder Umschalt-Klick mehrere Ebenen anhaken. Jede Aktion hier wirkt auf alle angehakten — und ist ein einziger Schritt im Verlauf.',
    selected: { one: '1 Ebene angehakt', other: '{count} Ebenen angehakt' },
    activeOnly: 'Nur die aktive Ebene',
    clear: 'Häkchen entfernen',
    smooth: 'Glätten',
    round: 'Ecken runden',
    radius: 'Radius in mm',
    removeSmall: 'Kleinteile entfernen',
    smallEdge: 'Kleiner als (mm)',
    lineStyle: 'Linienart setzen',
    apply: 'Setzen',
    nothing: 'Da war nichts zu tun.',
    donePaths: { one: '1 Pfad geändert, auf {layers} Ebenen.', other: '{count} Pfade geändert, auf {layers} Ebenen.' },
    doneRemoved: { one: '1 Kleinteil entfernt.', other: '{count} Kleinteile entfernt.' },
    doneLayers: { one: '1 Ebene geändert.', other: '{count} Ebenen geändert.' },
    skippedPaths: { one: '1 Pfad wurde übersprungen.', other: '{count} Pfade wurden übersprungen.' },
    skippedLayers: { one: '1 Ebene wurde übersprungen.', other: '{count} Ebenen wurden übersprungen.' },
  },
}
